/**
 * Custom JavaScript for Cabling Plus Shopify Theme
 * =================================================
 */

/* ============================================
   Multi-Category Filter Block
   File: blocks/_multi-category-filter.liquid
   ============================================ */

(function() {
  'use strict';

  /**
   * Initialize Multi-Category Filter functionality
   * Handles submenu toggles and animations
   */
  function initMultiCategoryFilter() {
    const filters = document.querySelectorAll('[data-multi-category-filter]');

    filters.forEach(filter => {
      // Handle submenu toggle buttons
      const toggleButtons = filter.querySelectorAll('.multi-category-filter__toggle');

      toggleButtons.forEach(button => {
        button.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();

          const isExpanded = this.getAttribute('aria-expanded') === 'true';
          // Submenu is a sibling of item-wrapper, so look in the parent <li>
          const parentLi = this.closest('.multi-category-filter__item, .navPage-subMenu-item');
          const submenu = parentLi?.querySelector('.multi-category-filter__submenu');

          if (!submenu) return;

          // Toggle the state
          this.setAttribute('aria-expanded', !isExpanded);

          if (isExpanded) {
            // Closing
            submenu.style.maxHeight = submenu.scrollHeight + 'px';
            submenu.offsetHeight; // Force reflow
            submenu.style.maxHeight = '0';
            submenu.classList.remove('is-open');

            setTimeout(() => {
              submenu.style.display = 'none';
              submenu.style.maxHeight = '';
            }, 300);
          } else {
            // Opening
            submenu.style.display = 'block';
            submenu.classList.add('is-open');
            submenu.style.maxHeight = '0';
            submenu.offsetHeight; // Force reflow
            submenu.style.maxHeight = submenu.scrollHeight + 'px';

            setTimeout(() => {
              submenu.style.maxHeight = '';
            }, 300);
          }
        });
      });

      // Auto-scroll to active category on page load
      const activeItem = filter.querySelector('.current-cate');
      if (activeItem) {
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          const container = activeItem.closest('.multi-category-filter__content');
          if (container) {
            const itemTop = activeItem.offsetTop;
            const containerHeight = container.clientHeight;
            if (itemTop > containerHeight) {
              container.scrollTop = itemTop - (containerHeight / 2);
            }
          }
        }, 100);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMultiCategoryFilter);
  } else {
    initMultiCategoryFilter();
  }

  // Re-initialize after AJAX updates (for Shopify's dynamic sections)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('[data-multi-category-filter]')) {
      initMultiCategoryFilter();
    }
  });

})();

/* End Multi-Category Filter Block */

/* ============================================
   Bulk Order Grid
   File: sections/bulk-order-grid.liquid
   File: snippets/bulk-order-product-row.liquid
   ============================================ */

(function() {
  'use strict';

  // SINGLETON PATTERN: Prevent multiple instances
  let bulkOrderInstance = null;
  // Flag to prevent duplicate event listeners
  let eventsAlreadyBound = false;

  /**
   * Bulk Order Manager
   * Handles AJAX add to cart, quantity controls, search, infinite scroll, and clear cart
   */
  class BulkOrderManager {
    constructor() {
      // SECURITY FIX: Prevent duplicate instances causing multiple event handlers
      if (bulkOrderInstance) {
        return bulkOrderInstance;
      }

      // Get config from namespaced object (Liquid sets window.bulkOrderConfig[sectionId])
      this.config = this.getConfig();
      this.cartState = new Map(); // Track cart quantities by variant ID
      this.searchTimeout = null;
      this.isLoading = false;
      this.cartStateKey = 'bulkOrderCartState'; // sessionStorage key

      this.init();
      bulkOrderInstance = this;
      // Expose instance globally for debugging and external access
      window.bulkOrderInstance = this;
    }

    /**
     * Get config from namespaced bulkOrderConfig object
     */
    getConfig() {
      const configObj = window.bulkOrderConfig || {};
      // Config is namespaced by section ID - find the first available config
      const sectionIds = Object.keys(configObj);
      if (sectionIds.length > 0) {
        return configObj[sectionIds[0]];
      }
      return {};
    }

    /**
     * Destroy instance (for cleanup)
     */
    destroy() {
      bulkOrderInstance = null;
    }

    init() {
      if (!this.config.sectionId) return;

      // Restore cart state from sessionStorage before API call
      this.restoreCartState();

      this.bindEvents();
      this.loadCartState();

      // Listen for external cart updates
      this.bindCartUpdateListeners();

      if (this.config.enableSearch) {
        this.initSearch();
      }

      if (this.config.enableInfiniteScroll) {
        this.initInfiniteScroll();
      }
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // CRITICAL: Prevent duplicate event listeners
      if (eventsAlreadyBound) {
        return;
      }
      eventsAlreadyBound = true;

      // Quantity change buttons
      document.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-qty-change]');
        if (qtyBtn) {
          e.preventDefault();
          this.handleQuantityChange(qtyBtn);
        }

        // Add to cart buttons
        const addBtn = e.target.closest('[data-add-to-cart]');
        if (addBtn) {
          e.preventDefault();
          this.handleAddToCart(addBtn);
        }

        // Clear cart button
        const clearBtn = e.target.closest('[data-clear-cart]');
        if (clearBtn) {
          e.preventDefault();
          this.handleClearCart();
        }

        // Load more button
        const loadMoreBtn = e.target.closest('[data-load-more-button]');
        if (loadMoreBtn) {
          e.preventDefault();
          this.handleLoadMore(loadMoreBtn);
        }
      });

      // Quantity input direct change
      document.addEventListener('change', (e) => {
        const qtyInput = e.target.closest('[data-qty-input]');
        if (qtyInput) {
          this.validateQuantityInput(qtyInput);
        }
      });

      // Variant select change
      document.addEventListener('change', (e) => {
        const variantSelect = e.target.closest('[data-variant-select]');
        if (variantSelect) {
          this.handleVariantChange(variantSelect);
        }
      });
    }

    /**
     * Handle quantity +/- buttons
     */
    handleQuantityChange(btn) {
      const productId = btn.dataset.productId;
      const change = parseInt(btn.dataset.qtyChange);
      const input = document.querySelector(`[data-qty-input][data-product-id="${productId}"]`);

      if (!input) return;

      let currentQty = parseInt(input.value) || 0;
      let newQty = Math.max(0, Math.min(999, currentQty + change));

      input.value = newQty;
    }

    /**
     * Validate quantity input
     */
    validateQuantityInput(input) {
      let value = parseInt(input.value) || 0;
      value = Math.max(0, Math.min(999, value));
      input.value = value;
    }

    /**
     * Handle variant selection change
     */
    handleVariantChange(select) {
      const productId = select.dataset.productId;
      const selectedOption = select.options[select.selectedIndex];
      const variantId = selectedOption.value;

      // Update price display
      const priceDisplay = select.parentElement.closest('[data-product-id]').querySelector('[data-price-display]');
      if (priceDisplay) {
        const basePrice = Number(selectedOption.dataset.dealerPrice || selectedOption.dataset.price);
        const priceContainer = priceDisplay.closest('[data-price-container]');
        const salePercent = Number(priceContainer?.dataset.cpSalePercent) || 0;
        const price = basePrice - Math.round(basePrice * salePercent / 100);
        const originalPrice = priceContainer?.querySelector('[data-cp-sale-original]');
        if (originalPrice) {
          originalPrice.hidden = !(salePercent > 0 && basePrice > 0);
          originalPrice.textContent = this.formatMoney(basePrice);
        }

        // Format price (Shopify price is in cents)
        const formattedPrice = this.formatMoney(price);
        priceDisplay.textContent = formattedPrice;
      }

      // Update add to cart button variant ID
      const addBtn = select.parentElement.closest('[data-product-id]').querySelector('[data-add-to-cart]');
      if (addBtn) {
        addBtn.dataset.variantId = variantId;
      }
    }

    /**
     * Get sections to render for cart updates
     * Matches the theme's cart-drawer getSectionsToRender
     */
    getSectionsToRender() {
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    /**
     * Handle add to cart
     */
    async handleAddToCart(btn) {
      const productId = btn.dataset.productId;
      const variantId = btn.dataset.variantId;
      const qtyInput = document.querySelector(`[data-qty-input][data-product-id="${productId}"]`);
      const quantity = parseInt(qtyInput?.value) || 0;

      // SECURITY FIX: Validate variant ID before cart operations
      if (!variantId || !/^\d+$/.test(variantId)) {
        this.showNotification('Invalid product variant', 'error');
        return;
      }

      if (quantity <= 0) {
        alert('Please enter a quantity greater than 0');
        return;
      }

      if (quantity > 999) {
        this.showNotification('Maximum quantity is 999', 'error');
        return;
      }

      // Show loading state
      this.setButtonLoading(btn, true);

      try {
        // Request section rendering for cart drawer and cart icon bubble
        const sectionsToRender = this.getSectionsToRender();

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest' // SECURITY FIX: CSRF protection
          },
          credentials: 'same-origin', // SECURITY FIX: Ensure cookies are sent
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: quantity
            }],
            sections: sectionsToRender,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();

        if (!response.ok) {
          // Shopify returns error details in the response body
          const errorMsg = data.description || data.message || 'Failed to add to cart';
          throw new Error(errorMsg);
        }

        // Refresh cart state from API to get accurate quantities
        await this.refreshCartState();

        // Reset quantity input to default
        if (qtyInput) qtyInput.value = 1;

        // Update all cart status displays with fresh data
        this.updateAllCartStatuses();

        // Update sections (cart drawer and cart icon) without opening the drawer
        if (data.sections) {
          this.updateSectionsWithoutDrawerOpen(data.sections);
        }

        // Show success notification
        this.showNotification('Added to cart successfully!', 'success');

        // Dispatch custom event for external listeners
        this.dispatchCartUpdatedEvent();

      } catch (error) {
        console.error('Add to cart error:', error);
        this.showNotification(error.message || 'Failed to add to cart. Please try again.', 'error');
      } finally {
        this.setButtonLoading(btn, false);
      }
    }

    /**
     * Set button loading state
     */
    setButtonLoading(btn, isLoading) {
      const text = btn.querySelector('.bulk-order-add-btn-text, .bulk-order-card__add-btn-text');
      const spinner = btn.querySelector('.bulk-order-add-btn-spinner, .bulk-order-card__add-btn-spinner');

      if (isLoading) {
        btn.disabled = true;
        if (text) text.hidden = true;
        if (spinner) spinner.hidden = false;
      } else {
        btn.disabled = false;
        if (text) text.hidden = false;
        if (spinner) spinner.hidden = true;
      }
    }

    /**
     * Update cart state tracking
     * @deprecated Use refreshCartState() instead for accurate data
     */
    updateCartState(variantId, quantityAdded) {
      const currentQty = this.cartState.get(variantId) || 0;
      this.cartState.set(variantId, currentQty + quantityAdded);
    }

    /**
     * Show cart status below button
     * Fixed to handle multiple product rows with same product ID but different variants
     */
    showCartStatus(productId, variantId) {
      // Get ALL rows/cards for this product (use specific container selectors, not button which also has data-product-id)
      const rows = document.querySelectorAll(`tr.bulk-order-row[data-product-id="${productId}"], .bulk-order-card[data-product-id="${productId}"]`);
      if (!rows.length) return;

      rows.forEach(row => {
        const statusEl = row.querySelector('[data-cart-status]');
        const qtyEl = row.querySelector('[data-cart-quantity]');

        // Only update the row that matches this specific variant
        const rowVariantId = row.querySelector('[data-add-to-cart]')?.dataset.variantId;

        if (statusEl && qtyEl && rowVariantId === variantId) {
          const totalQty = this.cartState.get(variantId) || 0;
          qtyEl.textContent = totalQty;
          statusEl.hidden = totalQty === 0;
        }
      });
    }

    /**
     * Load current cart state
     */
    async loadCartState() {
      try {
        const response = await fetch('/cart.js', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const cart = await response.json();

        cart.items.forEach(item => {
          this.cartState.set(item.variant_id.toString(), item.quantity);
        });

        // Persist to sessionStorage
        this.persistCartState();

        // Update all cart status displays
        this.updateAllCartStatuses();

      } catch (error) {
        console.error('Failed to load cart state:', error);
      }
    }

    /**
     * Refresh cart state from API (called after cart updates)
     * This ensures we have accurate quantities after add-to-cart operations
     */
    async refreshCartState() {
      try {
        const response = await fetch('/cart.js', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const cart = await response.json();

        // Clear and rebuild cartState from fresh API data
        this.cartState.clear();

        cart.items.forEach(item => {
          this.cartState.set(item.variant_id.toString(), item.quantity);
        });

        // Persist updated state to sessionStorage
        this.persistCartState();

        return cart;

      } catch (error) {
        console.error('Failed to refresh cart state:', error);
        throw error;
      }
    }

    /**
     * Persist cart state to sessionStorage
     */
    persistCartState() {
      try {
        const stateObject = Object.fromEntries(this.cartState);
        sessionStorage.setItem(this.cartStateKey, JSON.stringify(stateObject));
      } catch (error) {
        console.error('Failed to persist cart state:', error);
      }
    }

    /**
     * Restore cart state from sessionStorage
     */
    restoreCartState() {
      try {
        const stored = sessionStorage.getItem(this.cartStateKey);
        if (stored) {
          const stateObject = JSON.parse(stored);
          this.cartState = new Map(Object.entries(stateObject));
          // Update displays immediately with cached data
          this.updateAllCartStatuses();
        }
      } catch (error) {
        console.error('Failed to restore cart state:', error);
      }
    }

    /**
     * Bind listeners for external cart updates
     */
    bindCartUpdateListeners() {
      // Listen for custom cart:updated events (from theme or other scripts)
      document.addEventListener('cart:updated', async () => {
        await this.refreshCartState();
        this.updateAllCartStatuses();
      });

      // Listen for Shopify section reload events
      document.addEventListener('shopify:section:load', async (event) => {
        if (event.target.querySelector('[data-bulk-order-container]')) {
          await this.refreshCartState();
          this.updateAllCartStatuses();
        }
      });
    }

    /**
     * Dispatch custom event when cart is updated
     */
    dispatchCartUpdatedEvent() {
      const event = new CustomEvent('cart:updated', {
        detail: {
          cartState: Object.fromEntries(this.cartState)
        }
      });
      document.dispatchEvent(event);
    }

    /**
     * Update all cart status displays
     * FIXED: Handles grouped products with variant breakdown showing each variant's quantity
     */
    updateAllCartStatuses() {
      // First, hide all cart statuses
      document.querySelectorAll('[data-cart-status]').forEach(el => {
        el.hidden = true;
      });

      // Handle grouped product rows specially
      this.updateGroupedProductCartStatuses();

      // Then, update each variant that has items in cart (for non-grouped rows)
      this.cartState.forEach((quantity, variantId) => {
        if (quantity <= 0) return;

        // Find all add-to-cart buttons with this variant ID
        const addButtons = document.querySelectorAll(`[data-add-to-cart][data-variant-id="${variantId}"]`);

        addButtons.forEach(button => {
          // Use specific selectors to find the row/card container (not the button itself which also has data-product-id)
          const row = button.closest('tr.bulk-order-row[data-product-id], .bulk-order-card[data-product-id]');
          if (!row) return;

          // Skip grouped rows - they're handled separately
          if (row.classList.contains('bulk-order-row--grouped') || row.classList.contains('bulk-order-card--grouped')) {
            return;
          }

          const statusEl = row.querySelector('[data-cart-status]');
          const qtyEl = row.querySelector('[data-cart-quantity]');

          if (statusEl && qtyEl) {
            qtyEl.textContent = quantity;
            statusEl.hidden = false;
          }
        });
      });
    }

    /**
     * Update cart status for grouped product rows
     * Shows breakdown by variant with option names
     */
    updateGroupedProductCartStatuses() {
      const groupedRows = document.querySelectorAll('.bulk-order-row--grouped, .bulk-order-card--grouped');

      groupedRows.forEach(row => {
        const statusEl = row.querySelector('[data-cart-status]');
        if (!statusEl) return;

        // Get all variant IDs for this group
        const variantIdsStr = row.dataset.groupVariantIds;
        if (!variantIdsStr) return;

        const variantIds = variantIdsStr.split(',');

        // Build cart status breakdown
        const cartItems = [];

        variantIds.forEach(variantId => {
          const qty = this.cartState.get(variantId);
          if (qty && qty > 0) {
            // Find the original hidden row for this variant to get option info
            const originalRow = document.querySelector(`tr.bulk-order-row[data-product-id][data-grouped-product="true"] [data-add-to-cart][data-variant-id="${variantId}"]`)?.closest('tr');

            let optionLabel = '';
            if (originalRow) {
              // Get the product title which includes options
              const titleEl = originalRow.querySelector('.bulk-order-product-title a');
              if (titleEl) {
                const fullTitle = titleEl.textContent.trim();
                // Extract options from title (after the first delimiter)
                const delimiters = [' - ', ', ', ' – ', ' — '];
                for (const delim of delimiters) {
                  if (fullTitle.includes(delim)) {
                    const parts = fullTitle.split(delim);
                    if (parts.length > 1) {
                      optionLabel = parts.slice(1).join(delim);
                      break;
                    }
                  }
                }
              }
            }

            cartItems.push({
              qty,
              label: optionLabel || variantId
            });
          }
        });

        if (cartItems.length > 0) {
          // Build HTML for cart status breakdown
          const qtyEl = statusEl.querySelector('[data-cart-quantity]');
          if (qtyEl) {
            if (cartItems.length === 1) {
              // Single item - simple display
              qtyEl.innerHTML = `${cartItems[0].qty} ${cartItems[0].label}`;
            } else {
              // Multiple items - show breakdown
              const breakdown = cartItems.map(item =>
                `<span class="cart-status-line">${item.qty} ${item.label}</span>`
              ).join('');
              qtyEl.innerHTML = breakdown;
            }
            statusEl.hidden = false;
          }
        }
      });
    }

    /**
     * Update header cart count
     */
    async updateCartCount() {
      try {
        const response = await fetch('/cart.js', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const cart = await response.json();

        // Update cart count in header (common Shopify selectors)
        const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #cart-icon-bubble span');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count;
        });

      } catch (error) {
        console.error('Failed to update cart count:', error);
      }
    }

    /**
     * Update sections (cart drawer and cart icon) without opening the drawer
     * This updates the cart contents and header cart count dynamically
     * @param {Object} sections - The sections object from Shopify's response
     */
    updateSectionsWithoutDrawerOpen(sections) {
      const parser = new DOMParser();

      // Update cart-icon-bubble section (header cart count)
      if (sections['cart-icon-bubble']) {
        const iconBubbleHtml = sections['cart-icon-bubble'];
        const newDoc = parser.parseFromString(iconBubbleHtml, 'text/html');
        const newContent = newDoc.querySelector('.shopify-section')?.innerHTML;

        // Find and update the cart icon bubble in the DOM
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && newContent) {
          cartIconBubble.innerHTML = newContent;

          // Also update aria-label for accessibility
          const newCartCount = newDoc.querySelector('[data-cart-count]')?.textContent?.trim();
          const newCartText = newDoc.querySelector('[data-cart-text]')?.textContent?.trim();
          if (newCartText) {
            cartIconBubble.setAttribute('aria-label', newCartText);
          } else if (newCartCount) {
            cartIconBubble.setAttribute('aria-label', `${newCartCount} items`);
          }
        }
      }

      // Update cart-drawer section (cart contents) WITHOUT opening it
      if (sections['cart-drawer']) {
        const cartDrawerHtml = sections['cart-drawer'];
        const newDoc = parser.parseFromString(cartDrawerHtml, 'text/html');

        // Update cart drawer items
        const newCartDrawerItems = newDoc.querySelector('cart-drawer-items');
        const existingCartDrawerItems = document.querySelector('cart-drawer-items');

        if (newCartDrawerItems && existingCartDrawerItems) {
          // Update the cart items content
          const newForm = newCartDrawerItems.querySelector('#CartDrawer-Form');
          const existingForm = existingCartDrawerItems.querySelector('#CartDrawer-Form');
          if (newForm && existingForm) {
            existingForm.innerHTML = newForm.innerHTML;
          }

          // Update the cart items table
          const newCartItems = newCartDrawerItems.querySelector('#CartDrawer-CartItems');
          const existingCartItems = existingCartDrawerItems.querySelector('#CartDrawer-CartItems');
          if (newCartItems && existingCartItems) {
            existingCartItems.innerHTML = newCartItems.innerHTML;
          }
        }

        // Update cart drawer footer (totals)
        const newFooter = newDoc.querySelector('.cart-drawer__footer');
        const existingFooter = document.querySelector('.cart-drawer__footer');
        if (newFooter && existingFooter) {
          existingFooter.innerHTML = newFooter.innerHTML;
        }

        // Remove is-empty class if cart now has items
        const cartDrawer = document.querySelector('cart-drawer');
        const drawerInner = document.querySelector('.drawer__inner');
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
        }
        if (drawerInner) {
          drawerInner.classList.remove('is-empty');
        }
      }
    }

    /**
     * Handle clear cart
     */
    async handleClearCart() {
      if (!confirm('Are you sure you want to clear your entire cart?')) {
        return;
      }

      try {
        // Request section rendering for cart drawer and cart icon bubble
        const sectionsToRender = this.getSectionsToRender();

        const response = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest' // SECURITY FIX: CSRF protection
          },
          credentials: 'same-origin' // SECURITY FIX: Ensure cookies are sent
        });

        if (!response.ok) {
          throw new Error('Failed to clear cart');
        }

        // Refresh cart state from API
        await this.refreshCartState();

        // Update all displays
        this.updateAllCartStatuses();

        // Fetch updated sections manually since clear.js doesn't support section rendering
        const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionsToRender.join(',')}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });

        if (sectionsResponse.ok) {
          const sectionsData = await sectionsResponse.json();
          this.updateSectionsWithoutDrawerOpen(sectionsData);
        }

        this.showNotification('Cart cleared successfully!', 'success');

        // Dispatch custom event
        this.dispatchCartUpdatedEvent();

      } catch (error) {
        console.error('Clear cart error:', error);
        this.showNotification('Failed to clear cart. Please try again.', 'error');
      }
    }

    /**
     * Initialize search functionality
     */
    initSearch() {
      const searchInput = document.querySelector('[data-search-input]');
      if (!searchInput) return;

      searchInput.addEventListener('input', (e) => {
        clearTimeout(this.searchTimeout);

        this.searchTimeout = setTimeout(() => {
          this.performSearch(e.target.value);
        }, 300); // Debounce 300ms
      });
    }

    /**
     * Perform product search
     */
    performSearch(query) {
      const searchTerm = query.toLowerCase().trim();
      const rows = document.querySelectorAll('[data-product-handle]');

      rows.forEach(row => {
        const handle = row.dataset.productHandle;
        const title = row.querySelector('.bulk-order-product-title, .bulk-order-card__title')?.textContent.toLowerCase() || '';

        if (searchTerm === '' || title.includes(searchTerm) || handle.includes(searchTerm)) {
          row.style.display = '';
        } else {
          row.style.display = 'none';
        }
      });

      // Check if any products are visible
      const visibleRows = Array.from(rows).filter(row => row.style.display !== 'none');
      this.toggleEmptyState(visibleRows.length === 0);
    }

    /**
     * Toggle empty state message
     */
    toggleEmptyState(show) {
      let emptyState = document.querySelector('.bulk-order-search-empty');

      if (show && !emptyState) {
        emptyState = document.createElement('div');
        emptyState.className = 'bulk-order-search-empty';
        // SECURITY FIX: Use textContent instead of innerHTML to prevent XSS
        const paragraph = document.createElement('p');
        paragraph.textContent = 'No products match your search.';
        emptyState.appendChild(paragraph);

        const container = document.querySelector('[data-bulk-order-container]');
        if (container) {
          container.appendChild(emptyState);
        }
      } else if (!show && emptyState) {
        emptyState.remove();
      }
    }

    /**
     * Initialize infinite scroll
     */
    initInfiniteScroll() {
      const loadMoreBtn = document.querySelector('[data-load-more-button]');
      if (!loadMoreBtn) return;

      // Optionally add intersection observer for auto-load
      // For now, keeping manual button click as per requirements
    }

    /**
     * Handle load more products
     */
    async handleLoadMore(btn) {
      if (this.isLoading) return;

      const nextUrl = btn.dataset.nextUrl;
      if (!nextUrl) return;

      this.isLoading = true;
      this.showLoading(true);

      try {
        const response = await fetch(nextUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();

        // Parse HTML response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Get new products
        const newTableRows = doc.querySelectorAll('[data-product-table] tbody tr');
        const newCards = doc.querySelectorAll('[data-product-cards] .bulk-order-card');

        // Append to existing containers
        const existingTableBody = document.querySelector('[data-product-table] tbody');
        const existingCards = document.querySelector('[data-product-cards]');

        newTableRows.forEach(row => {
          if (existingTableBody) {
            existingTableBody.appendChild(row.cloneNode(true));
          }
        });

        newCards.forEach(card => {
          if (existingCards) {
            existingCards.appendChild(card.cloneNode(true));
          }
        });

        // Update load more button
        const newLoadMoreBtn = doc.querySelector('[data-load-more-button]');
        if (newLoadMoreBtn) {
          btn.dataset.nextUrl = newLoadMoreBtn.dataset.nextUrl;
        } else {
          // No more pages
          btn.closest('[data-load-more-container]')?.remove();
        }

      } catch (error) {
        console.error('Load more error:', error);
        this.showNotification('Failed to load more products.', 'error');
      } finally {
        this.isLoading = false;
        this.showLoading(false);
      }
    }

    /**
     * Show/hide loading indicator
     */
    showLoading(show) {
      const loader = document.querySelector('[data-loading]');
      if (loader) {
        loader.hidden = !show;
      }
    }

    /**
     * Show notification toast
     */
    showNotification(message, type = 'success') {
      // Check if theme has existing notification system
      if (typeof window.theme !== 'undefined' && window.theme.showNotification) {
        window.theme.showNotification(message, type);
        return;
      }

      // Fallback: Simple toast
      const toast = document.createElement('div');
      toast.className = `bulk-order-toast bulk-order-toast--${type}`;
      toast.textContent = message;
      toast.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        padding: 16px 24px;
        background: ${type === 'success' ? '#69C69C' : '#D12442'};
        color: white;
        border-radius: 4px;
        z-index: 9999;
        animation: slideIn 0.3s ease;
      `;

      document.body.appendChild(toast);

      setTimeout(() => {
        toast.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    }

    /**
     * Format money (fallback if Shopify money format not available)
     */
    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }

      // Fallback formatting
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }
  }

  // Safe initialization function
  function initBulkOrder() {
    if (document.querySelector('[data-bulk-order-container]') && !bulkOrderInstance) {
      new BulkOrderManager();
    }
  }

  /**
   * Robust initialization that handles deferred script loading
   * Uses multiple strategies to ensure initialization happens at the right time
   */
  function robustInit() {
    // Strategy 1: If DOM is already complete, use requestAnimationFrame to wait for paint
    if (document.readyState === 'complete') {
      requestAnimationFrame(() => {
        requestAnimationFrame(initBulkOrder);
      });
    }
    // Strategy 2: If DOM is interactive (parsed but not all resources loaded)
    else if (document.readyState === 'interactive') {
      // Small delay to let other deferred scripts execute first
      setTimeout(initBulkOrder, 10);
    }
    // Strategy 3: Wait for DOMContentLoaded
    else {
      document.addEventListener('DOMContentLoaded', () => {
        // Small delay to let other deferred scripts execute first
        setTimeout(initBulkOrder, 10);
      });
    }

    // Strategy 4: Fallback on window load (all resources including images loaded)
    window.addEventListener('load', () => {
      if (!bulkOrderInstance && document.querySelector('[data-bulk-order-container]')) {
        initBulkOrder();
      }
    });
  }

  // Start robust initialization
  robustInit();

  // Re-initialize after AJAX section updates
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-bulk-order-container]')) {
      initBulkOrder();
    }
  });

})();

/* End Bulk Order Grid */

/* ============================================
   Product Group Manager (Virtual Variants)
   File: sections/main-collection-bulk-order.liquid
   File: snippets/bulk-order-product-group.liquid

   AJAX-Based Product Loading:
   Fetches ALL products from collection via JSON endpoint to enable
   grouping across pagination boundaries. Products on different pages
   can now be merged into virtual variant groups.
   ============================================ */

(function() {
  'use strict';

  /**
   * Product Group Manager
   * Handles virtual variants by grouping simple products with similar names
   * Provides swatch selection UI with availability matrix
   *
   * NEW: AJAX-based loading fetches all products before grouping,
   * solving the pagination limitation where products on different
   * pages couldn't be grouped together.
   */
  class ProductGroupManager {
    constructor() {
      // Configuration
      this.config = {
        // Common length/size patterns to normalize
        // FIXED: Added more flexible patterns for cable lengths
        // NOTE: Added 't' as typo for 'ft' since some products have "500t" instead of "500ft"
        lengthPatterns: [
          { regex: /^(\d+(?:,\d+)?(?:\.\d+)?)\s*(?:ft|feet|foot|'|t)$/i, unit: 'ft' },  // Added 't' for typos
          { regex: /^(\d+(?:,\d+)?(?:\.\d+)?)\s*(?:m|meter|meters)$/i, unit: 'm' },
          { regex: /^(\d+(?:,\d+)?(?:\.\d+)?)\s*(?:in|inch|inches|")$/i, unit: 'in' },
          { regex: /^(\d+(?:,\d+)?(?:\.\d+)?)\s*(?:cm|centimeter|centimeters)$/i, unit: 'cm' },
          { regex: /^(\d+(?:,\d+)?(?:\.\d+)?)\s*(?:yd|yard|yards)$/i, unit: 'yd' },
          { regex: /^(\d+(?:,\d+)?(?:\.\d+)?)\s*(?:mm|millimeter|millimeters)$/i, unit: 'mm' },
          // Box/spool patterns for cable products
          { regex: /^(\d+(?:,\d+)?(?:\.\d+)?)\s*(?:ft|feet|foot|t)?\s*(?:box|spool|roll|reel)$/i, unit: 'ft box' }
        ],
        // Common color keywords
        colorKeywords: [
          'black', 'white', 'blue', 'red', 'green', 'yellow', 'orange', 'purple',
          'pink', 'gray', 'grey', 'brown', 'silver', 'gold', 'beige', 'tan',
          'navy', 'teal', 'aqua', 'maroon', 'olive', 'lime', 'cyan', 'magenta',
          'ivory', 'coral', 'violet', 'indigo', 'turquoise', 'natural', 'clear',
          'chrome', 'brass', 'copper', 'bronze', 'nickel', 'zinc', 'aluminum'
        ],
        // Delimiter patterns for parsing (order matters - most specific first)
        delimiters: [' - ', ' – ', ' — ', ', '],
        // Debug mode - set to true to see detailed grouping info
        debug: true,
        // AJAX configuration
        useAjaxLoading: true,  // Enable AJAX-based product loading
        productsPerPage: 500   // Max products per AJAX request
      };

      this.productGroups = new Map(); // Base name → grouped products
      this.optionTypes = new Map(); // Track option types (color, length, etc.)
      this.allProducts = []; // All products fetched via AJAX
      this.collectionHandle = null;
      this.isDealer = false;

      this.init();
    }

    async init() {
      // Get collection handle from the section wrapper
      const container = document.querySelector('[data-bulk-order-container]');
      if (!container) return;

      const sectionWrapper = container.closest('[data-collection-handle]');
      if (sectionWrapper) {
        this.collectionHandle = sectionWrapper.dataset.collectionHandle;
        this.isDealer = sectionWrapper.dataset.isDealer === 'true';
      }

      // If AJAX loading is enabled and we have a collection handle, fetch all products
      if (this.config.useAjaxLoading && this.collectionHandle) {
        await this.fetchAndProcessProducts();
      } else {
        // Fallback: Process products from DOM (original behavior)
        this.processProducts();
      }
    }

    /**
     * Fetch all products from the JSON endpoint and process them
     * This enables grouping across pagination boundaries
     */
    async fetchAndProcessProducts() {
      const container = document.querySelector('[data-bulk-order-container]');
      if (!container) return;

      try {
        // Show loading state
        this.showLoadingState(container);

        // Fetch all products from JSON endpoint
        const products = await this.fetchAllProducts();

        if (products.length === 0) {
          console.warn('ProductGroupManager: No products fetched via AJAX, falling back to DOM');
          this.hideLoadingState(container);
          this.processProducts();
          return;
        }

        console.log(`ProductGroupManager: Fetched ${products.length} products via AJAX`);

        // Group products
        this.groupProducts(products);

        // Render grouped products
        this.renderGroupedProductsFromAjax(container, products);

        // Hide loading state
        this.hideLoadingState(container);

        // Update product count display
        this.updateProductCount(products.length);

      } catch (error) {
        console.error('ProductGroupManager: AJAX fetch failed, falling back to DOM', error);
        this.hideLoadingState(container);
        // Fallback to DOM-based processing
        this.processProducts();
      }
    }

    /**
     * Fetch all products from the JSON endpoint
     * Handles pagination if collection has more than 500 products
     */
    async fetchAllProducts() {
      const allProducts = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const url = `/collections/${this.collectionHandle}?view=bulk-order-data&page=${currentPage}`;

        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // Add products from this page
        if (data.products && Array.isArray(data.products)) {
          data.products.forEach(product => {
            // Transform JSON data to match our product data structure
            const productData = this.transformJsonProduct(product);
            if (productData) {
              allProducts.push(productData);
            }
          });
        }

        // Check if there are more pages
        if (data.pagination && data.pagination.current_page < data.pagination.pages) {
          currentPage++;
        } else {
          hasMorePages = false;
        }
      }

      this.allProducts = allProducts;
      return allProducts;
    }

    /**
     * Transform JSON product data to match our internal format
     */
    transformJsonProduct(jsonProduct) {
      const parsed = this.parseTitle(jsonProduct.title);

      return {
        id: jsonProduct.id.toString(),
        handle: jsonProduct.handle,
        fullTitle: jsonProduct.title,
        baseName: parsed.baseName,
        options: parsed.options,
        normalizedOptions: parsed.normalizedOptions,
        price: jsonProduct.display_price_formatted || jsonProduct.price_formatted,
        priceRaw: jsonProduct.display_price || jsonProduct.price,
        variantId: jsonProduct.current_variant?.id?.toString() || '',
        available: jsonProduct.available,
        url: jsonProduct.url,
        image: jsonProduct.featured_image,
        imageAlt: jsonProduct.featured_image_alt,
        description: jsonProduct.description,
        partNumber: jsonProduct.part_number,
        specSheetUrl: jsonProduct.spec_sheet_url,
        variants: jsonProduct.variants || [],
        tags: jsonProduct.tags || [],
        hasOnlyDefaultVariant: jsonProduct.has_only_default_variant,
        // No element reference since we're loading via AJAX
        element: null
      };
    }

    /**
     * Show loading state on the container
     */
    showLoadingState(container) {
      // Show the loading overlay that already exists in the section
      const loadingOverlay = container.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('loading-overlay--active');
      }

      // Hide the product grid while loading
      const productTable = container.querySelector('.bulk-order-table');
      const productCards = container.querySelector('.bulk-order-cards');
      if (productTable) productTable.style.opacity = '0.3';
      if (productCards) productCards.style.opacity = '0.3';
    }

    /**
     * Hide loading state
     */
    hideLoadingState(container) {
      const loadingOverlay = container.querySelector('.loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.remove('loading-overlay--active');
      }

      const productTable = container.querySelector('.bulk-order-table');
      const productCards = container.querySelector('.bulk-order-cards');
      if (productTable) productTable.style.opacity = '1';
      if (productCards) productCards.style.opacity = '1';
    }

    /**
     * Update product count display with actual grouped count
     */
    updateProductCount(totalProducts) {
      // Calculate grouped count
      let groupedCount = 0;
      this.productGroups.forEach(group => {
        groupedCount++; // Each group counts as 1 display row
      });

      // Also count singleton products
      const singletonCount = totalProducts - Array.from(this.productGroups.values())
        .reduce((sum, g) => sum + (g.products.length > 1 ? g.products.length : 0), 0);

      // Update product count display if it exists
      const productCountEl = document.querySelector('#ProductCount, .product-count__text span');
      if (productCountEl && this.config.debug) {
        console.log(`ProductGroupManager: ${totalProducts} products → ${this.productGroups.size} display rows`);
      }
    }

    /**
     * Render grouped products from AJAX data
     * This creates the UI from fetched JSON data
     */
    renderGroupedProductsFromAjax(container, products) {
      // Get existing rows in the DOM for reference
      const existingRows = container.querySelectorAll('.bulk-order-row[data-product-id]');
      const existingCards = container.querySelectorAll('.bulk-order-card[data-product-id]');

      // Create a map of product ID to DOM element
      const domProductMap = new Map();
      existingRows.forEach(row => {
        domProductMap.set(row.dataset.productId, row);
      });

      // Associate DOM elements with AJAX products
      products.forEach(product => {
        const domElement = domProductMap.get(product.id);
        if (domElement) {
          product.element = domElement;
        }
      });

      // Now render grouped products using existing DOM elements
      this.renderGroupedProducts();
    }

    /**
     * Process all products on the page and group them
     * FIXED: Only select desktop rows (.bulk-order-row) to avoid
     * duplicating products from both desktop and mobile views
     */
    processProducts() {
      // Only target desktop table rows - mobile cards are hidden via CSS at 750px+
      // This prevents the same product from being collected twice
      const productRows = document.querySelectorAll('.bulk-order-row[data-product-id][data-product-handle]');
      if (!productRows.length) return;

      // Collect product data
      const products = [];
      productRows.forEach(row => {
        const productData = this.extractProductData(row);
        if (productData) {
          products.push(productData);
        }
      });

      // Group products by parsed base name
      this.groupProducts(products);

      // Render grouped products with swatches
      this.renderGroupedProducts();
    }

    /**
     * Extract product data from a DOM row
     */
    extractProductData(row) {
      const productId = row.dataset.productId;
      const productHandle = row.dataset.productHandle;
      const titleEl = row.querySelector('.bulk-order-product-title a, .bulk-order-card__title a');
      const priceEl = row.querySelector('[data-price-display]');
      const variantSelect = row.querySelector('[data-variant-select]');
      const addBtn = row.querySelector('[data-add-to-cart]');

      if (!titleEl) return null;

      const fullTitle = titleEl.textContent.trim();
      const parsed = this.parseTitle(fullTitle);

      return {
        id: productId,
        handle: productHandle,
        fullTitle: fullTitle,
        baseName: parsed.baseName,
        options: parsed.options,
        normalizedOptions: parsed.normalizedOptions,
        price: priceEl?.textContent?.trim() || '',
        variantId: addBtn?.dataset.variantId || variantSelect?.value || '',
        available: !addBtn?.disabled,
        element: row
      };
    }

    /**
     * Parse product title into base name and options
     * Handles inconsistent formats like:
     * - "Product Name - Option1 - Option2"
     * - "Product Name, Color, Size"
     * - "Product Name - 25 ft"
     */
    parseTitle(title) {
      let baseName = title;
      let options = [];
      let normalizedOptions = [];

      // Find the best delimiter to use
      let bestDelimiter = null;
      let maxParts = 0;

      for (const delimiter of this.config.delimiters) {
        const parts = title.split(delimiter);
        if (parts.length > maxParts) {
          maxParts = parts.length;
          bestDelimiter = delimiter;
        }
      }

      if (bestDelimiter && maxParts > 1) {
        const parts = title.split(bestDelimiter).map(p => p.trim());

        // Find where the base name ends and options begin
        // Strategy: options are typically at the end and are shorter/identifiable
        let optionStartIndex = this.findOptionStartIndex(parts);

        baseName = parts.slice(0, optionStartIndex).join(bestDelimiter).trim();
        const rawOptions = parts.slice(optionStartIndex);

        rawOptions.forEach(opt => {
          const normalized = this.normalizeOption(opt);
          options.push(opt);
          normalizedOptions.push(normalized);
        });
      } else {
        // Try to extract trailing length/size from single string
        const extracted = this.extractTrailingOption(title);
        if (extracted) {
          baseName = extracted.baseName;
          options = [extracted.option];
          normalizedOptions = [extracted.normalized];
        }
      }

      return { baseName, options, normalizedOptions };
    }

    /**
     * Find where options start in the parts array
     * Options are typically: colors, lengths, sizes
     * FIXED: Improved detection to handle more product naming patterns
     */
    findOptionStartIndex(parts) {
      // Start from the end and work backwards
      // Stop when we hit something that's clearly part of the product name

      // Strategy: Find the LAST part that looks like an option
      // Options are typically short, or are colors/lengths
      let lastOptionIndex = parts.length;

      for (let i = parts.length - 1; i >= 1; i--) {
        const part = parts[i].trim();
        const partLower = part.toLowerCase();

        // Check if this part looks like an option
        const isColor = this.isColorOption(partLower);
        const isLength = this.isLengthOption(part);
        const isSize = this.isSizeOption(part);

        // Options are typically short (less than 50 chars) and simple
        // Longer parts that aren't recognized as colors/lengths are likely product name components
        const isShortOption = part.length < 50;

        // A part is an option if it's a color, length, size, or is short enough
        const isLikelyOption = isColor || isLength || isSize || isShortOption;

        if (isLikelyOption) {
          lastOptionIndex = i;
        } else {
          // Hit a long part that's not a recognized option type
          // This is likely part of the product name, stop here
          break;
        }
      }

      return lastOptionIndex;
    }

    /**
     * Check if a string looks like a size option
     * Handles patterns like: Small, Medium, Large, XS, XL, 2XL, etc.
     */
    isSizeOption(str) {
      const sizePatterns = [
        /^(xs|s|m|l|xl|xxl|xxxl|2xl|3xl|4xl|5xl)$/i,
        /^(small|medium|large|x-large|xx-large)$/i,
        /^(one size|os|osfm)$/i,
        /^\d+(\.\d+)?\s*(pack|pc|pcs|pieces?|count|ct)$/i
      ];
      return sizePatterns.some(pattern => pattern.test(str.trim()));
    }

    /**
     * Check if a string is a color option
     */
    isColorOption(str) {
      const lower = str.toLowerCase().trim();
      return this.config.colorKeywords.some(color =>
        lower === color || lower.includes(color)
      );
    }

    /**
     * Check if a string is a length/size option
     */
    isLengthOption(str) {
      const trimmed = str.trim();
      return this.config.lengthPatterns.some(pattern =>
        pattern.regex.test(trimmed)
      );
    }

    /**
     * Extract trailing length option from a title
     * e.g., "Product Name 25ft" → { baseName: "Product Name", option: "25ft" }
     * FIXED: Uses updated patterns that match the whole string
     */
    extractTrailingOption(title) {
      // Try to find a trailing length pattern at the end of the title
      // First, try splitting by space and checking the last word(s)
      const words = title.trim().split(/\s+/);

      // Check last 1-2 words for length pattern
      for (let wordCount = 1; wordCount <= 2 && wordCount <= words.length; wordCount++) {
        const lastWords = words.slice(-wordCount).join(' ');

        for (const pattern of this.config.lengthPatterns) {
          const match = lastWords.match(pattern.regex);
          if (match) {
            const value = match[1].replace(',', ''); // Remove commas from numbers
            const baseName = words.slice(0, -wordCount).join(' ').trim();
            const normalized = `${value} ${pattern.unit}`;

            return {
              baseName,
              option: lastWords,
              normalized
            };
          }
        }
      }
      return null;
    }

    /**
     * Normalize an option string
     * Handles unit normalization and formatting
     */
    normalizeOption(option) {
      let normalized = option.trim();

      // Normalize length units
      for (const pattern of this.config.lengthPatterns) {
        const match = normalized.match(pattern.regex);
        if (match) {
          normalized = `${match[1]} ${pattern.unit}`;
          break;
        }
      }

      // Capitalize color names consistently
      if (this.isColorOption(normalized)) {
        normalized = this.capitalizeFirst(normalized.toLowerCase());
      }

      return normalized;
    }

    /**
     * Capitalize first letter
     */
    capitalizeFirst(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    /**
     * Detect the type of option (color, length, other)
     */
    detectOptionType(option) {
      if (this.isColorOption(option)) return 'color';
      if (this.isLengthOption(option)) return 'length';
      return 'other';
    }

    /**
     * Group products by their base name AND option count
     * Products with different numbers of options are kept separate to avoid misalignment
     */
    groupProducts(products) {
      this.productGroups.clear();

      // Debug: Track all parsed products
      if (this.config.debug) {
        console.group('ProductGroupManager: Parsing Details');
        products.forEach(product => {
          console.log(`"${product.fullTitle}" → Base: "${product.baseName}", Options: [${product.normalizedOptions.join(', ')}]`);
        });
        console.groupEnd();
      }

      products.forEach(product => {
        // Include option count in key to prevent grouping products with different option structures
        const optionCount = product.normalizedOptions.length;
        const key = `${product.baseName.toLowerCase()}__${optionCount}`;

        if (!this.productGroups.has(key)) {
          this.productGroups.set(key, {
            baseName: product.baseName,
            optionCount: optionCount,
            products: [],
            optionSets: new Map(), // option type → Set of values
            availabilityMatrix: new Map() // option combo key → product
          });
        }

        const group = this.productGroups.get(key);
        group.products.push(product);

        // Track option values by type
        product.normalizedOptions.forEach((opt, idx) => {
          const type = this.detectOptionType(opt);
          const typeKey = `option${idx}_${type}`;

          if (!group.optionSets.has(typeKey)) {
            group.optionSets.set(typeKey, new Set());
          }
          group.optionSets.get(typeKey).add(opt);
        });

        // Build availability matrix
        const comboKey = product.normalizedOptions.join('|');
        group.availabilityMatrix.set(comboKey, product);
      });

      // Post-process: identify common options (pre-selected/disabled)
      this.identifyCommonOptions();

      // Debug: Show grouping summary
      if (this.config.debug) {
        console.group('ProductGroupManager: Grouping Summary');
        let groupsWithMultiple = 0;
        this.productGroups.forEach((group, key) => {
          if (group.products.length > 1) {
            groupsWithMultiple++;
            console.log(`✓ "${key}" → ${group.products.length} products will be merged`);
          } else {
            console.log(`✗ "${key}" → Only 1 product (won't merge)`);
          }
        });
        console.log(`Total: ${groupsWithMultiple} groups will be merged, ${this.productGroups.size - groupsWithMultiple} singletons`);
        console.groupEnd();
      }

      console.log('ProductGroupManager: Grouped products', this.productGroups);
    }

    /**
     * Identify options that are the same across ALL products in a group
     * These should be pre-selected and disabled
     */
    identifyCommonOptions() {
      this.productGroups.forEach((group, key) => {
        group.commonOptions = [];
        group.variableOptions = [];

        group.optionSets.forEach((values, typeKey) => {
          if (values.size === 1) {
            // Only one value - this is a common/fixed option
            group.commonOptions.push({
              type: typeKey,
              value: Array.from(values)[0]
            });
          } else if (values.size > 1) {
            // Multiple values - this is a variable option
            group.variableOptions.push({
              type: typeKey,
              values: Array.from(values),
              optionType: this.detectOptionType(Array.from(values)[0])
            });
          }
        });
      });
    }

    /**
     * Get availability for a specific option selection
     * Returns which other options are still available
     *
     * FIXED: Now checks availability for each option independently
     * When checking which values are available for option A,
     * we only filter by OTHER selected options, not option A itself.
     * This prevents all options from being disabled when selections
     * don't have a perfect intersection.
     */
    getAvailability(group, selectedOptions) {
      const availability = new Map();

      // Initialize all options as unavailable
      group.variableOptions.forEach(opt => {
        availability.set(opt.type, new Set());
      });

      // For each variable option type, check which values are available
      group.variableOptions.forEach(varOpt => {
        const optType = varOpt.type;
        const optIndex = parseInt(optType.replace(/\D/g, ''));

        // Check each product in the availability matrix
        group.availabilityMatrix.forEach((product, comboKey) => {
          const productOptions = comboKey.split('|');

          // Check if this product matches OTHER selections (not this option type)
          // This allows us to show available options without requiring a perfect combo
          let matchesOtherSelections = true;

          Object.entries(selectedOptions).forEach(([type, value]) => {
            // Skip the option type we're checking availability for
            if (type === optType) return;

            const selIndex = parseInt(type.replace(/\D/g, ''));
            if (productOptions[selIndex] && productOptions[selIndex] !== value) {
              matchesOtherSelections = false;
            }
          });

          if (matchesOtherSelections) {
            // This product matches the other selections
            // Add its value for this option type as available
            if (productOptions[optIndex]) {
              availability.get(optType)?.add(productOptions[optIndex]);
            }
          }
        });
      });

      return availability;
    }

    /**
     * Find product matching the selected options
     * FIXED: Build combo key including both variable AND common options
     * to match the format used in availabilityMatrix
     */
    findMatchingProduct(group, selectedOptions) {
      // Get all option types in order (both common and variable)
      const allOptionTypes = [];

      // Collect all option types from common options
      group.commonOptions.forEach(opt => {
        allOptionTypes.push({ type: opt.type, value: opt.value });
      });

      // Collect all option types from variable options
      group.variableOptions.forEach(opt => {
        const selectedValue = selectedOptions[opt.type];
        if (selectedValue) {
          allOptionTypes.push({ type: opt.type, value: selectedValue });
        }
      });

      // Sort by option index (option0, option1, etc.) to match normalizedOptions order
      allOptionTypes.sort((a, b) => {
        const indexA = parseInt(a.type.match(/option(\d+)/)?.[1] || '0');
        const indexB = parseInt(b.type.match(/option(\d+)/)?.[1] || '0');
        return indexA - indexB;
      });

      // Build combo key from all options
      const comboKey = allOptionTypes.map(opt => opt.value).join('|');

      return group.availabilityMatrix.get(comboKey) || null;
    }

    /**
     * Render grouped products with swatch UI
     * UPDATED: Handles products from AJAX that may not have DOM elements
     * Products without elements (from other pages) contribute their options
     * to the group but are not hidden (since they're not on this page)
     */
    renderGroupedProducts() {
      // Only process groups with multiple products
      this.productGroups.forEach((group, key) => {
        if (group.products.length <= 1) return;
        if (group.variableOptions.length === 0) return;

        // Sort products so those with DOM elements come first
        // This ensures we have a template for the grouped row
        group.products.sort((a, b) => {
          if (a.element && !b.element) return -1;
          if (!a.element && b.element) return 1;
          return 0;
        });

        // Find the first product with a DOM element (for template)
        const templateProduct = group.products.find(p => p.element);

        if (!templateProduct) {
          // No products on this page have DOM elements
          // This means all products in this group are on other pages
          // Skip rendering for this group on this page
          if (this.config.debug) {
            console.log(`ProductGroupManager: Skipping group "${group.baseName}" - no products on current page`);
          }
          return;
        }

        // Hide individual product rows that exist on this page
        group.products.forEach(product => {
          if (product.element) {
            product.element.style.display = 'none';
            product.element.dataset.groupedProduct = 'true';
          }
        });

        // Create grouped product row using the template
        const groupedRow = this.createGroupedProductRow(group, templateProduct);

        // Insert before the template product's row
        templateProduct.element.parentNode.insertBefore(groupedRow, templateProduct.element);
      });
    }

    /**
     * Create the grouped product row with swatches
     * @param {Object} group - The product group
     * @param {Object} templateProduct - Product with DOM element to use as template
     */
    createGroupedProductRow(group, templateProduct = null) {
      // Use provided template or fall back to first product
      const firstProduct = templateProduct || group.products.find(p => p.element) || group.products[0];

      if (!firstProduct.element) {
        console.error('ProductGroupManager: Cannot create grouped row - no template element');
        return document.createElement('tr'); // Return empty row as fallback
      }

      const isTableRow = firstProduct.element.tagName === 'TR';

      if (isTableRow) {
        return this.createTableGroupRow(group);
      } else {
        return this.createCardGroupRow(group);
      }
    }

    /**
     * Create table row for grouped product
     */
    createTableGroupRow(group) {
      const firstProduct = group.products[0];
      const tr = document.createElement('tr');
      tr.className = 'bulk-order-row bulk-order-row--grouped';
      tr.dataset.productGroupKey = group.baseName.toLowerCase();

      // Clone the first product's row structure
      const template = firstProduct.element.cloneNode(true);

      // Update title to show base name only
      const titleLink = template.querySelector('.bulk-order-product-title a');
      if (titleLink) {
        titleLink.textContent = group.baseName;
      }

      // Remove variant selector if exists
      const variantSelect = template.querySelector('.bulk-order-variants');
      if (variantSelect) {
        variantSelect.remove();
      }

      // Add swatches BEFORE the part number/meta row (not at the end)
      const productInfo = template.querySelector('.bulk-order-product-info');
      if (productInfo) {
        const swatchContainer = this.createSwatchContainer(group);
        const metaRow = productInfo.querySelector('.bulk-order-product-meta-row');

        if (metaRow) {
          // Insert swatches before the meta row
          productInfo.insertBefore(swatchContainer, metaRow);
          // Add a divider line between swatches and meta row
          const divider = document.createElement('hr');
          divider.className = 'bulk-order-options-divider';
          productInfo.insertBefore(divider, metaRow);
        } else {
          // Fallback: append at the end if no meta row exists
          productInfo.appendChild(swatchContainer);
        }
      }

      // Copy all cells to new row
      Array.from(template.querySelectorAll('td')).forEach(td => {
        tr.appendChild(td.cloneNode(true));
      });

      // Bind swatch events
      this.bindSwatchEvents(tr, group);

      return tr;
    }

    /**
     * Create card for grouped product (mobile)
     */
    createCardGroupRow(group) {
      const firstProduct = group.products[0];
      const card = firstProduct.element.cloneNode(true);
      card.className = 'bulk-order-card bulk-order-card--grouped';
      card.dataset.productGroupKey = group.baseName.toLowerCase();

      // Update title
      const titleLink = card.querySelector('.bulk-order-card__title a');
      if (titleLink) {
        titleLink.textContent = group.baseName;
      }

      // Remove variant selector
      const variantSelect = card.querySelector('.bulk-order-card__variants');
      if (variantSelect) {
        variantSelect.remove();
      }

      // Add swatches
      const info = card.querySelector('.bulk-order-card__info');
      if (info) {
        const swatchContainer = this.createSwatchContainer(group);
        info.appendChild(swatchContainer);
      }

      // Bind swatch events
      this.bindSwatchEvents(card, group);

      return card;
    }

    /**
     * Create swatch container with all option types
     * Includes separators between option groups and styled tags for common options
     */
    createSwatchContainer(group) {
      const container = document.createElement('div');
      container.className = 'bulk-order-swatches';

      let hasAddedElement = false;

      // Show common options as tags (pre-selected, non-interactive)
      if (group.commonOptions.length > 0) {
        const commonGroup = document.createElement('div');
        commonGroup.className = 'bulk-order-swatch-group bulk-order-swatch-group--common';

        group.commonOptions.forEach(opt => {
          const tag = document.createElement('span');
          tag.className = 'bulk-order-option-tag';
          tag.textContent = opt.value;
          tag.dataset.optionType = opt.type;
          commonGroup.appendChild(tag);
        });

        container.appendChild(commonGroup);
        hasAddedElement = true;
      }

      // Show variable options (selectable) with separators
      group.variableOptions.forEach((opt, idx) => {
        // Add separator before this group if not the first element
        if (hasAddedElement) {
          const separator = document.createElement('span');
          separator.className = 'bulk-order-swatch-separator';
          separator.setAttribute('aria-hidden', 'true');
          container.appendChild(separator);
        }

        const optionGroup = document.createElement('div');
        optionGroup.className = 'bulk-order-swatch-group';
        optionGroup.dataset.optionType = opt.type;
        optionGroup.dataset.optionStyle = opt.optionType; // 'color', 'length', 'other'

        // Create swatches for each value
        opt.values.forEach((value, valueIdx) => {
          const swatch = this.createSwatch(value, opt.optionType, valueIdx === 0);
          swatch.dataset.optionType = opt.type;
          swatch.dataset.optionValue = value;
          optionGroup.appendChild(swatch);
        });

        container.appendChild(optionGroup);
        hasAddedElement = true;
      });

      return container;
    }

    /**
     * Create individual swatch element
     */
    createSwatch(value, optionType, isSelected) {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = `bulk-order-swatch bulk-order-swatch--${optionType}`;

      if (isSelected) {
        swatch.classList.add('bulk-order-swatch--selected');
      }

      if (optionType === 'color') {
        // Color swatch (circle)
        swatch.classList.add('bulk-order-swatch--circle');
        swatch.style.backgroundColor = this.getColorValue(value);
        swatch.setAttribute('aria-label', value);
        swatch.title = value;
      } else {
        // Pill swatch (for length/other)
        swatch.classList.add('bulk-order-swatch--pill');
        swatch.textContent = value;
      }

      return swatch;
    }

    /**
     * Get CSS color value from color name
     */
    getColorValue(colorName) {
      const colorMap = {
        'black': '#000000',
        'white': '#FFFFFF',
        'blue': '#0066CC',
        'red': '#CC0000',
        'green': '#009900',
        'yellow': '#FFCC00',
        'orange': '#FF6600',
        'purple': '#660099',
        'pink': '#FF66B2',
        'gray': '#808080',
        'grey': '#808080',
        'brown': '#663300',
        'silver': '#C0C0C0',
        'gold': '#FFD700',
        'navy': '#000080',
        'teal': '#008080',
        'aqua': '#00FFFF',
        'maroon': '#800000',
        'olive': '#808000',
        'lime': '#00FF00',
        'cyan': '#00FFFF',
        'magenta': '#FF00FF',
        'beige': '#F5F5DC',
        'tan': '#D2B48C',
        'ivory': '#FFFFF0',
        'coral': '#FF7F50',
        'violet': '#EE82EE',
        'indigo': '#4B0082',
        'turquoise': '#40E0D0'
      };

      const lower = colorName.toLowerCase().trim();
      return colorMap[lower] || '#CCCCCC';
    }

    /**
     * Bind swatch click events
     * FIXED: Auto-select first options and update product display on init
     */
    bindSwatchEvents(container, group) {
      const swatches = container.querySelectorAll('.bulk-order-swatch:not(.bulk-order-swatch--disabled)');

      swatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleSwatchClick(swatch, container, group);
        });
      });

      // Mark container as grouped product
      container.dataset.requiresOptionSelection = 'true';
      container.dataset.variableOptionsCount = group.variableOptions.length;

      // Auto-select first option in each variable option group and update display
      // This ensures the grouped product row shows the correct product data on load
      this.autoSelectFirstOptions(container, group);
    }

    /**
     * Auto-select first options and update product display
     * Called on init to ensure grouped products have valid initial state
     */
    autoSelectFirstOptions(container, group) {
      // Ensure first swatch in each group is selected
      container.querySelectorAll('.bulk-order-swatch-group').forEach(optGroup => {
        const firstSwatch = optGroup.querySelector('.bulk-order-swatch');
        if (firstSwatch && !optGroup.querySelector('.bulk-order-swatch--selected')) {
          firstSwatch.classList.add('bulk-order-swatch--selected');
        }
      });

      // Get current selections (should now have all first options)
      const selections = this.getCurrentSelections(container);

      // Mark options as selected
      container.dataset.optionsSelected = 'true';

      // Find and display the matching product
      const matchingProduct = this.findMatchingProduct(group, selections);
      if (matchingProduct) {
        this.updateProductDisplay(container, matchingProduct);

        // Store reference to group for cart status updates
        container.dataset.productGroupKey = group.baseName.toLowerCase();

        // Store all variant IDs in the group for cart status tracking
        const allVariantIds = group.products.map(p => p.variantId).filter(Boolean);
        container.dataset.groupVariantIds = allVariantIds.join(',');
      }
    }

    /**
     * Handle swatch selection
     */
    handleSwatchClick(swatch, container, group) {
      const optionType = swatch.dataset.optionType;
      const optionValue = swatch.dataset.optionValue;

      // Update selection in this option group
      const optionGroup = swatch.closest('.bulk-order-swatch-group');
      optionGroup.querySelectorAll('.bulk-order-swatch').forEach(s => {
        s.classList.remove('bulk-order-swatch--selected');
      });
      swatch.classList.add('bulk-order-swatch--selected');

      // Get all current selections
      const selections = this.getCurrentSelections(container);

      // Update availability of other options
      this.updateSwatchAvailability(container, group, selections);

      // Check if all options are now selected
      const requiredCount = group.variableOptions.length;
      const selectedCount = Object.keys(selections).length;
      container.dataset.optionsSelected = (selectedCount >= requiredCount) ? 'true' : 'false';

      // Hide error message when user makes a selection
      const errorMsg = container.querySelector('.bulk-order-options-error');
      if (errorMsg) {
        errorMsg.hidden = true;
      }

      // Find and update the matching product
      const matchingProduct = this.findMatchingProduct(group, selections);
      if (matchingProduct) {
        this.updateProductDisplay(container, matchingProduct);
      }
    }

    /**
     * Get current swatch selections
     */
    getCurrentSelections(container) {
      const selections = {};

      container.querySelectorAll('.bulk-order-swatch-group').forEach(group => {
        const optionType = group.dataset.optionType;
        const selected = group.querySelector('.bulk-order-swatch--selected');

        if (optionType && selected) {
          selections[optionType] = selected.dataset.optionValue;
        }
      });

      return selections;
    }

    /**
     * Update swatch availability based on current selections
     */
    updateSwatchAvailability(container, group, selections) {
      const availability = this.getAvailability(group, selections);

      container.querySelectorAll('.bulk-order-swatch-group').forEach(optGroup => {
        const optionType = optGroup.dataset.optionType;
        if (!optionType) return;

        const availableValues = availability.get(optionType) || new Set();

        optGroup.querySelectorAll('.bulk-order-swatch').forEach(swatch => {
          const value = swatch.dataset.optionValue;
          const isAvailable = availableValues.has(value);

          swatch.classList.toggle('bulk-order-swatch--unavailable', !isAvailable);
          swatch.disabled = !isAvailable;
        });
      });

      // Auto-select if only one option available
      container.querySelectorAll('.bulk-order-swatch-group').forEach(optGroup => {
        const availableSwatches = optGroup.querySelectorAll('.bulk-order-swatch:not(.bulk-order-swatch--unavailable)');
        const selectedSwatch = optGroup.querySelector('.bulk-order-swatch--selected');

        if (availableSwatches.length === 1 && !selectedSwatch?.classList.contains('bulk-order-swatch--selected')) {
          availableSwatches[0].classList.add('bulk-order-swatch--selected');
        }
      });
    }

    /**
     * Update product display (price, variant ID, availability, image)
     * UPDATED: Handles products from AJAX that may not have DOM elements
     * Uses JSON data when element is not available
     */
    updateProductDisplay(container, product) {
      // Update product image
      const imageCell = container.querySelector('.bulk-order-cell--image, .bulk-order-card__image');
      if (imageCell) {
        if (product.element) {
          // Copy from original product element if available
          const originalImageCell = product.element.querySelector('.bulk-order-cell--image, .bulk-order-card__image');
          if (originalImageCell) {
            imageCell.innerHTML = originalImageCell.innerHTML;
          }
        } else if (product.image) {
          // Use AJAX data to update image
          const imgLink = imageCell.querySelector('a');
          const img = imageCell.querySelector('img');
          if (imgLink) {
            imgLink.href = product.url;
            imgLink.setAttribute('aria-label', `View ${product.fullTitle}`);
          }
          if (img) {
            img.src = product.image;
            img.alt = product.imageAlt || product.fullTitle;
          }
        }
      }

      // Update price display
      const priceDisplay = container.querySelector('[data-price-display]');
      if (priceDisplay && product.price) {
        priceDisplay.textContent = product.price;
      }

      // Update add-to-cart button
      const addBtn = container.querySelector('[data-add-to-cart]');
      if (addBtn) {
        addBtn.dataset.variantId = product.variantId;
        addBtn.dataset.productId = product.id;
        addBtn.disabled = !product.available;

        const btnText = addBtn.querySelector('.bulk-order-add-btn-text, .bulk-order-card__add-btn-text');
        if (btnText) {
          btnText.textContent = product.available ? 'Add to Cart' : 'Sold Out';
        }
      }

      // Update quantity input product ID
      const qtyInput = container.querySelector('[data-qty-input]');
      if (qtyInput) {
        qtyInput.dataset.productId = product.id;
      }

      // Update quantity buttons
      container.querySelectorAll('[data-qty-change]').forEach(btn => {
        btn.dataset.productId = product.id;
      });

      // Update part number if displayed
      const partNumberDisplay = container.querySelector('.bulk-order-product-part-badge__value');
      if (partNumberDisplay) {
        if (product.element) {
          const originalPartNumber = product.element.querySelector('.bulk-order-product-part-badge__value');
          if (originalPartNumber) {
            partNumberDisplay.textContent = originalPartNumber.textContent;
          }
        } else if (product.partNumber) {
          // Use AJAX data
          partNumberDisplay.textContent = product.partNumber;
        }
      }

      // Update product title text to show full title with options
      const titleLink = container.querySelector('.bulk-order-product-title a, .bulk-order-card__title a');
      if (titleLink && product.fullTitle) {
        titleLink.textContent = product.fullTitle;
      }

      // Update all product links (image, title, etc.)
      const productLinks = container.querySelectorAll('a[href*="/products/"]');
      productLinks.forEach(link => {
        link.href = product.url || `/products/${product.handle}`;
      });
    }
  }

  // Initialize ProductGroupManager
  let productGroupInstance = null;

  function initProductGroupManager() {
    // Only init if container exists and we haven't already
    if (!document.querySelector('[data-bulk-order-container]')) return;
    if (productGroupInstance) return;

    // Check that product rows exist and have data (deferred scripts may not have populated them yet)
    const rows = document.querySelectorAll('.bulk-order-row[data-product-id]');
    if (rows.length === 0) {
      // No rows yet, retry after a short delay
      console.log('ProductGroupManager: Waiting for product rows...');
      setTimeout(initProductGroupManager, 100);
      return;
    }

    productGroupInstance = new ProductGroupManager();
    window.productGroupInstance = productGroupInstance;
    console.log('ProductGroupManager: Initialized with', rows.length, 'products');
  }

  /**
   * Robust initialization that handles deferred script loading
   * ProductGroupManager MUST wait for BulkOrderManager to be ready
   * and for all product rows to be rendered
   */
  function robustProductGroupInit() {
    // Strategy 1: If DOM is already complete, use requestAnimationFrame
    if (document.readyState === 'complete') {
      // Double rAF to ensure painting is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Additional delay to ensure BulkOrderManager initializes first
          setTimeout(initProductGroupManager, 50);
        });
      });
    }
    // Strategy 2: If DOM is interactive
    else if (document.readyState === 'interactive') {
      // Wait a bit longer than BulkOrderManager
      setTimeout(initProductGroupManager, 100);
    }
    // Strategy 3: Wait for DOMContentLoaded
    else {
      document.addEventListener('DOMContentLoaded', () => {
        // Wait a bit longer than BulkOrderManager (which uses 10ms)
        setTimeout(initProductGroupManager, 100);
      });
    }

    // Strategy 4: Fallback on window load
    window.addEventListener('load', () => {
      if (!productGroupInstance && document.querySelector('[data-bulk-order-container]')) {
        setTimeout(initProductGroupManager, 50);
      }
    });
  }

  // Start robust initialization
  robustProductGroupInit();

  // Re-initialize after AJAX section updates
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-bulk-order-container]')) {
      productGroupInstance = null;
      setTimeout(initProductGroupManager, 100);
    }
  });

})();

/* End Product Group Manager */

/* ============================================
   Mega Menu / Dropdown Close Delay
   File: snippets/header-menu-vertical.liquid
   File: snippets/header-nav-mega.liquid
   File: assets/details-disclosure.js (overrides onHover)
   ============================================ */

(function() {
  'use strict';

  /**
   * Add close delay to dropdown menus and mega menus
   * Overrides the onHover method of DropdownDetails to add a 1 second delay
   * before closing submenus/mega menus, improving UX by preventing
   * accidental menu closures when moving the mouse.
   */
  const CLOSE_DELAY_MS = 0; // No delay - instant close

  function initDropdownCloseDelay() {
    // Get the DropdownDetails class from the customElements registry
    const DropdownDetailsClass = customElements.get('dropdown-details');

    if (!DropdownDetailsClass) {
      console.warn('DropdownCloseDelay: DropdownDetails custom element not found');
      return;
    }

    // Check if already patched to avoid double-patching
    if (DropdownDetailsClass.prototype._closeDelayPatched) {
      return;
    }

    // Store the original onHover method
    const originalOnHover = DropdownDetailsClass.prototype.onHover;

    // Override the onHover method to add close delay
    DropdownDetailsClass.prototype.onHover = function(event) {
      // Clear any existing RAF or timeout
      cancelAnimationFrame(this._hoverRaf);
      if (this._closeTimeout) {
        clearTimeout(this._closeTimeout);
        this._closeTimeout = null;
      }

      if (event.type === 'mouseenter') {
        // Open immediately - no delay on open
        this._hoverRaf = requestAnimationFrame(() => {
          this.isOpen = true;
        });
      } else {
        // Close with delay - gives user time to move mouse to submenu
        this._closeTimeout = setTimeout(() => {
          this._hoverRaf = requestAnimationFrame(() => {
            this.isOpen = false;
          });
        }, CLOSE_DELAY_MS);
      }
    };

    // Mark as patched
    DropdownDetailsClass.prototype._closeDelayPatched = true;

    console.log('DropdownCloseDelay: Initialized with ' + CLOSE_DELAY_MS + 'ms delay');
  }

  // Also patch Safari-specific hover handler if present
  function patchSafariHoverHandler() {
    // Safari hover handlers are set up in an IIFE in details-disclosure.js
    // We need to add close delay to the mouseleave handlers for Safari
    // This is done by wrapping existing event listeners

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (!isSafari) return;

    // For Safari, we add a delegated handler with delay
    document.addEventListener('mouseleave', function(event) {
      const details = event.target.closest('details[activate-event="hover"]');
      if (!details) return;

      // Clear any existing close timeout
      if (details._safariCloseTimeout) {
        clearTimeout(details._safariCloseTimeout);
      }

      // Set new timeout for delayed close
      details._safariCloseTimeout = setTimeout(() => {
        // Only close if still not hovered
        if (!details.matches(':hover')) {
          details.setAttribute('open', 'false');
        }
      }, CLOSE_DELAY_MS);
    }, true); // Use capture phase

    // Cancel close timeout on mouseenter
    document.addEventListener('mouseenter', function(event) {
      const details = event.target.closest('details[activate-event="hover"]');
      if (!details) return;

      if (details._safariCloseTimeout) {
        clearTimeout(details._safariCloseTimeout);
        details._safariCloseTimeout = null;
      }
    }, true);
  }

  // Initialize after DOM and custom elements are ready
  function init() {
    // Small delay to ensure custom elements are registered
    setTimeout(() => {
      initDropdownCloseDelay();
      patchSafariHoverHandler();
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* End Mega Menu / Dropdown Close Delay */

/* ==========================================================================
   PROJECT LIST MANAGER
   Multi-list functionality for Project Lists (formerly Wishlist)

   This class provides the core data layer for managing multiple named
   project lists, replacing the legacy single-wishlist system.

   Related files:
   - snippets/project-list-modal.liquid (UI for list management)
   - sections/main-wishlist-page.liquid (Project list page display)
   - snippets/global-script.liquid (Configuration injection)
   - assets/global.js (Legacy Wishlist class - backward compatibility)

   localStorage Keys:
   - projectLists: New multi-list data structure (v2)
   - wishlistItem: Legacy single-list array (v1) - migrated on first load
   - wishlistItem_backup: Backup of legacy data for rollback

   Data Structure: See /Non-Shopify-Files/docs/Wishlist/DataStructure.md
   Migration Strategy: See /Non-Shopify-Files/docs/Wishlist/DataMigration.md
   ========================================================================== */

(function() {
  'use strict';

  /* --------------------------------------------------------------------------
     CONSTANTS
     -------------------------------------------------------------------------- */

  const STORAGE_KEYS = {
    NEW: 'projectLists',
    LEGACY: 'wishlistItem',
    BACKUP: 'wishlistItem_backup'
  };

  const SCHEMA = {
    VERSION: 2,
    VERSION_STRING: '2.0.0'
  };

  const LIMITS = {
    MAX_LISTS: 20,
    MAX_PRODUCTS_PER_LIST: 500,
    MAX_LIST_NAME_LENGTH: 50
  };

  const DEFAULT_METADATA = {
    color: '#4A90D9',
    icon: 'folder'
  };

  /* ===========================================
     SYNC CONFIGURATION
     Settings for cross-device customer metafield sync
     =========================================== */
  const SYNC_CONFIG = {
    // Set this to your Cloudflare Worker URL after deployment
    // Example: 'https://project-list-sync.your-account.workers.dev/sync'
    endpoint: window.projectListSyncEndpoint || null,
    // Debounce time in ms before syncing changes
    debounceMs: 2000,
    // Enable/disable sync (set to true after deploying worker)
    enabled: false
  };

  /* --------------------------------------------------------------------------
     PROJECT LIST MANAGER CLASS
     Core data layer for multi-list project list functionality
     -------------------------------------------------------------------------- */

  class ProjectListManager {

    /* ========================================================================
       CONSTRUCTOR & INITIALIZATION
       ======================================================================== */

    /**
     * Initialize the ProjectListManager
     * Automatically checks for and performs migration from legacy format
     * Now supports customer metafield sync for cross-device persistence
     */
    constructor() {
      this.data = null;
      this.isInitialized = false;
      this.syncPending = false;
      this.syncTimeout = null;

      // Get customer data from Liquid injection
      this.customerData = window.projectListCustomerData || {
        isLoggedIn: false,
        customerId: null,
        metafieldData: null,
        hasMetafield: false,
        lastSynced: null
      };

      // Initialize data (migrate if needed or create fresh)
      this._initialize();

      // Listen for storage events from other tabs
      this._bindStorageEvents();

      console.log('[ProjectListManager] Initialized', {
        isLoggedIn: this.customerData.isLoggedIn,
        hasMetafield: this.customerData.hasMetafield,
        syncEnabled: SYNC_CONFIG.enabled
      });
    }

    /**
     * Initialize data from localStorage
     * Handles migration from legacy format if needed
     * Merges customer metafield data for cross-device sync
     * @private
     */
    _initialize() {
      try {
        // Check if new format exists and is valid in localStorage
        const localData = this._loadData();
        const localDataValid = localData && this._validateData(localData).valid;

        // Check if customer has metafield data
        const metafieldData = this.customerData.metafieldData;
        const metafieldDataValid = metafieldData && this._validateData(metafieldData).valid;

        // Determine which data to use based on login state and data availability
        if (this.customerData.isLoggedIn && metafieldDataValid) {
          if (localDataValid) {
            // Merge: Customer is logged in with both local and cloud data
            this.data = this._mergeData(localData, metafieldData);
            console.log('[ProjectListManager] Merged local and cloud data');
          } else {
            // Use metafield data (cloud-only)
            this.data = metafieldData;
            console.log('[ProjectListManager] Loaded cloud data from metafield');
          }
          this._saveData(); // Save merged/cloud data to localStorage
          this.isInitialized = true;
          return;
        }

        // Not logged in or no metafield data - use local data flow
        if (localDataValid) {
          this.data = localData;
          this.isInitialized = true;
          console.log('[ProjectListManager] Loaded existing v2 data from localStorage');
          return;
        }

        // Check if migration is needed
        if (this._needsMigration()) {
          const migrationResult = this.migrateFromLegacy();
          if (migrationResult.success) {
            console.log(`[ProjectListManager] Migrated ${migrationResult.productCount} products from legacy format`);
          } else {
            console.warn('[ProjectListManager] Migration failed, initializing fresh data');
            this._initializeFreshData();
          }
        } else {
          // No existing data, initialize fresh
          this._initializeFreshData();
        }

        this.isInitialized = true;

      } catch (error) {
        console.error('[ProjectListManager] Initialization error:', error);
        this._initializeFreshData();
        this.isInitialized = true;
      }
    }

    /**
     * Merge local and metafield data
     * Uses the most recent version of each list based on updatedAt
     * @private
     * @param {Object} localData - Data from localStorage
     * @param {Object} cloudData - Data from customer metafield
     * @returns {Object} Merged data
     */
    _mergeData(localData, cloudData) {
      const merged = {
        version: SCHEMA.VERSION,
        schemaVersion: SCHEMA.VERSION_STRING,
        migratedAt: localData.migratedAt || cloudData.migratedAt,
        activeListId: localData.activeListId || cloudData.activeListId,
        lastModified: new Date().toISOString(),
        lists: []
      };

      // Create a map of all lists by ID
      const listMap = new Map();

      // Add cloud lists first
      if (cloudData.lists) {
        cloudData.lists.forEach(list => {
          listMap.set(list.id, { ...list, source: 'cloud' });
        });
      }

      // Merge local lists (newer versions win)
      if (localData.lists) {
        localData.lists.forEach(list => {
          const existing = listMap.get(list.id);
          if (!existing) {
            // New local list not in cloud
            listMap.set(list.id, { ...list, source: 'local' });
          } else {
            // Compare timestamps to determine which is newer
            const localTime = new Date(list.updatedAt || 0).getTime();
            const cloudTime = new Date(existing.updatedAt || 0).getTime();
            if (localTime > cloudTime) {
              listMap.set(list.id, { ...list, source: 'local' });
            }
          }
        });
      }

      // Convert map back to array and clean up source markers
      merged.lists = Array.from(listMap.values()).map(list => {
        const { source, ...cleanList } = list;
        return cleanList;
      });

      // Ensure we have at least one list
      if (merged.lists.length === 0) {
        const now = new Date().toISOString();
        const defaultListId = this._generateId();
        const defaultName = window.projectListConfig?.defaultListName || 'My First Project';
        merged.lists.push({
          id: defaultListId,
          name: defaultName,
          slug: this._generateSlug(defaultName),
          createdAt: now,
          updatedAt: now,
          isDefault: true,
          products: [],
          metadata: { ...DEFAULT_METADATA }
        });
        merged.activeListId = defaultListId;
      }

      // Validate activeListId exists
      const activeExists = merged.lists.some(l => l.id === merged.activeListId);
      if (!activeExists) {
        merged.activeListId = merged.lists[0].id;
      }

      console.log('[ProjectListManager] Merge complete:', {
        totalLists: merged.lists.length,
        activeListId: merged.activeListId
      });

      return merged;
    }

    /**
     * Initialize fresh data structure for new users
     * @private
     */
    _initializeFreshData() {
      const now = new Date().toISOString();
      const defaultListId = this._generateId();
      const defaultName = window.projectListConfig?.defaultListName || 'My First Project';

      this.data = {
        version: SCHEMA.VERSION,
        schemaVersion: SCHEMA.VERSION_STRING,
        migratedAt: null,
        activeListId: defaultListId,
        lastModified: now,
        lists: [
          {
            id: defaultListId,
            name: defaultName,
            slug: this._generateSlug(defaultName),
            createdAt: now,
            updatedAt: now,
            isDefault: true,
            products: [],
            metadata: { ...DEFAULT_METADATA }
          }
        ]
      };

      this._saveData();
      console.log('[ProjectListManager] Initialized fresh data structure');
    }

    /**
     * Bind storage events for cross-tab synchronization
     * @private
     */
    _bindStorageEvents() {
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEYS.NEW) {
          console.log('[ProjectListManager] Data changed in another tab, reloading');
          this.data = this._loadData();
          this._dispatchEvent('projectlist:synced', { source: 'external' });
        }
      });
    }

    /* ========================================================================
       STORAGE OPERATIONS
       ======================================================================== */

    /**
     * Load data from localStorage
     * @private
     * @returns {Object|null} Parsed data or null if not found/invalid
     */
    _loadData() {
      try {
        const stored = localStorage.getItem(STORAGE_KEYS.NEW);
        if (!stored) return null;
        return JSON.parse(stored);
      } catch (error) {
        console.error('[ProjectListManager] Error loading data:', error);
        return null;
      }
    }

    /**
     * Save data to localStorage
     * Triggers debounced cloud sync for logged-in customers
     * @private
     * @returns {boolean} Success status
     */
    _saveData() {
      try {
        this.data.lastModified = new Date().toISOString();
        localStorage.setItem(STORAGE_KEYS.NEW, JSON.stringify(this.data));

        // Trigger cloud sync for logged-in customers
        if (this.customerData.isLoggedIn && SYNC_CONFIG.enabled) {
          this._queueCloudSync();
        }

        return true;
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          console.error('[ProjectListManager] localStorage quota exceeded');
          // Attempt cleanup and retry
          this._cleanupLegacyData();
          try {
            localStorage.setItem(STORAGE_KEYS.NEW, JSON.stringify(this.data));
            return true;
          } catch (retryError) {
            console.error('[ProjectListManager] Save failed after cleanup:', retryError);
            return false;
          }
        }
        console.error('[ProjectListManager] Save error:', error);
        return false;
      }
    }

    /**
     * Queue a debounced cloud sync
     * Prevents excessive API calls during rapid changes
     * @private
     */
    _queueCloudSync() {
      // Clear any pending sync
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout);
      }

      this.syncPending = true;

      // Queue new sync with debounce
      this.syncTimeout = setTimeout(() => {
        this._performCloudSync();
      }, SYNC_CONFIG.debounceMs);
    }

    /**
     * Perform the actual cloud sync to customer metafield
     * @private
     * @returns {Promise<Object>} Sync result
     */
    async _performCloudSync() {
      if (!this.customerData.isLoggedIn || !SYNC_CONFIG.endpoint) {
        this.syncPending = false;
        return { success: false, error: 'Not logged in or no sync endpoint' };
      }

      try {
        console.log('[ProjectListManager] Syncing to cloud...');

        const response = await fetch(SYNC_CONFIG.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customerId: this.customerData.customerId,
            projectLists: this.data
          })
        });

        const result = await response.json();

        if (result.success) {
          console.log('[ProjectListManager] Cloud sync successful:', result.updatedAt);
          this._dispatchEvent('projectlist:cloud-synced', {
            success: true,
            updatedAt: result.updatedAt
          });
        } else {
          console.error('[ProjectListManager] Cloud sync failed:', result.error);
          this._dispatchEvent('projectlist:cloud-sync-error', {
            error: result.error
          });
        }

        this.syncPending = false;
        return result;

      } catch (error) {
        console.error('[ProjectListManager] Cloud sync error:', error);
        this.syncPending = false;
        this._dispatchEvent('projectlist:cloud-sync-error', {
          error: error.message
        });
        return { success: false, error: error.message };
      }
    }

    /**
     * Force immediate cloud sync (bypasses debounce)
     * @returns {Promise<Object>} Sync result
     */
    async forceCloudSync() {
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout);
        this.syncTimeout = null;
      }
      return this._performCloudSync();
    }

    /**
     * Check if customer is logged in
     * @returns {boolean}
     */
    isCustomerLoggedIn() {
      return this.customerData.isLoggedIn;
    }

    /**
     * Get sync status
     * @returns {Object} Sync status info
     */
    getSyncStatus() {
      return {
        isLoggedIn: this.customerData.isLoggedIn,
        syncEnabled: SYNC_CONFIG.enabled,
        syncPending: this.syncPending,
        lastSynced: this.customerData.lastSynced
      };
    }

    /**
     * Clean up legacy data from localStorage
     * @private
     */
    _cleanupLegacyData() {
      try {
        localStorage.removeItem(STORAGE_KEYS.LEGACY);
        localStorage.removeItem(STORAGE_KEYS.BACKUP);
        console.log('[ProjectListManager] Cleaned up legacy data');
      } catch (error) {
        console.error('[ProjectListManager] Cleanup error:', error);
      }
    }

    /* ========================================================================
       ID & SLUG GENERATION
       ======================================================================== */

    /**
     * Generate a unique project list ID
     * Format: pl_ + 12 random alphanumeric characters
     * @private
     * @returns {string} Generated ID (e.g., 'pl_a1b2c3d4e5f6')
     */
    _generateId() {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let id = 'pl_';
      for (let i = 0; i < 12; i++) {
        id += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      // Ensure uniqueness within current lists
      if (this.data && this.data.lists) {
        const existingIds = this.data.lists.map(l => l.id);
        while (existingIds.includes(id)) {
          id = 'pl_';
          for (let i = 0; i < 12; i++) {
            id += chars.charAt(Math.floor(Math.random() * chars.length));
          }
        }
      }

      return id;
    }

    /**
     * Generate URL-safe slug from list name
     * @private
     * @param {string} name - List display name
     * @returns {string} URL-safe slug
     */
    _generateSlug(name) {
      return name
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')     // Remove special characters
        .replace(/\s+/g, '-')          // Replace spaces with hyphens
        .replace(/-+/g, '-')           // Replace multiple hyphens with single
        .substring(0, 60);             // Limit length
    }

    /* ========================================================================
       VALIDATION
       ======================================================================== */

    /**
     * Validate the entire data structure
     * @private
     * @param {Object} data - Data to validate
     * @returns {Object} { valid: boolean, errors: string[] }
     */
    _validateData(data) {
      const errors = [];

      // Check root structure
      if (!data || typeof data !== 'object') {
        errors.push('Data must be an object');
        return { valid: false, errors };
      }

      // Check version
      if (data.version !== SCHEMA.VERSION) {
        errors.push(`Invalid version: expected ${SCHEMA.VERSION}, got ${data.version}`);
      }

      // Check schemaVersion
      if (typeof data.schemaVersion !== 'string') {
        errors.push('schemaVersion must be a string');
      }

      // Check activeListId
      if (typeof data.activeListId !== 'string' || data.activeListId.length === 0) {
        errors.push('activeListId must be a non-empty string');
      }

      // Check lastModified
      if (typeof data.lastModified !== 'string') {
        errors.push('lastModified must be an ISO timestamp string');
      }

      // Check lists array
      if (!Array.isArray(data.lists)) {
        errors.push('lists must be an array');
        return { valid: false, errors };
      }

      if (data.lists.length === 0) {
        errors.push('lists array must have at least one list');
      }

      if (data.lists.length > LIMITS.MAX_LISTS) {
        errors.push(`lists array exceeds maximum of ${LIMITS.MAX_LISTS} lists`);
      }

      // Validate each list
      let defaultCount = 0;
      const listIds = new Set();
      const listNames = new Set();

      data.lists.forEach((list, index) => {
        // Check required fields
        if (!list.id || typeof list.id !== 'string') {
          errors.push(`List ${index}: missing or invalid id`);
        } else if (listIds.has(list.id)) {
          errors.push(`List ${index}: duplicate id "${list.id}"`);
        } else {
          listIds.add(list.id);
        }

        if (!list.name || typeof list.name !== 'string') {
          errors.push(`List ${index}: missing or invalid name`);
        } else {
          const nameLower = list.name.toLowerCase().trim();
          if (listNames.has(nameLower)) {
            errors.push(`List ${index}: duplicate name "${list.name}"`);
          } else {
            listNames.add(nameLower);
          }
        }

        if (!Array.isArray(list.products)) {
          errors.push(`List ${index}: products must be an array`);
        } else if (list.products.length > LIMITS.MAX_PRODUCTS_PER_LIST) {
          errors.push(`List ${index}: products exceeds maximum of ${LIMITS.MAX_PRODUCTS_PER_LIST}`);
        }

        if (list.isDefault === true) {
          defaultCount++;
        }
      });

      // Check exactly one default
      if (defaultCount !== 1) {
        errors.push(`Expected exactly 1 default list, found ${defaultCount}`);
      }

      // Check activeListId exists in lists
      if (data.activeListId && !listIds.has(data.activeListId)) {
        errors.push(`activeListId "${data.activeListId}" does not exist in lists`);
      }

      return {
        valid: errors.length === 0,
        errors
      };
    }

    /**
     * Validate a list name
     * @private
     * @param {string} name - Name to validate
     * @param {string|null} excludeListId - List ID to exclude from duplicate check
     * @returns {Object} { valid: boolean, error: string|null }
     */
    _validateListName(name, excludeListId = null) {
      if (!name || typeof name !== 'string') {
        return { valid: false, error: 'List name is required' };
      }

      const trimmed = name.trim();

      if (trimmed.length === 0) {
        return { valid: false, error: 'List name cannot be empty' };
      }

      if (trimmed.length > LIMITS.MAX_LIST_NAME_LENGTH) {
        return { valid: false, error: `List name cannot exceed ${LIMITS.MAX_LIST_NAME_LENGTH} characters` };
      }

      // Check for duplicate names (case-insensitive)
      const nameLower = trimmed.toLowerCase();
      const isDuplicate = this.data.lists.some(list =>
        list.name.toLowerCase() === nameLower && list.id !== excludeListId
      );

      if (isDuplicate) {
        return { valid: false, error: 'A list with this name already exists' };
      }

      return { valid: true, error: null };
    }

    /**
     * Validate a product handle
     * @private
     * @param {string} handle - Handle to validate
     * @returns {Object} { valid: boolean, error: string|null }
     */
    _validateProductHandle(handle) {
      if (!handle || typeof handle !== 'string') {
        return { valid: false, error: 'Product handle is required' };
      }

      const trimmed = handle.trim();

      if (trimmed.length === 0) {
        return { valid: false, error: 'Product handle cannot be empty' };
      }

      return { valid: true, error: null };
    }

    /* ========================================================================
       MIGRATION METHODS
       ======================================================================== */

    /**
     * Check if migration from legacy format is needed
     * @private
     * @returns {boolean} True if migration is needed
     */
    _needsMigration() {
      try {
        // Check if new format exists and is valid
        const newData = this._loadData();
        if (newData && this._validateData(newData).valid) {
          return false;
        }

        // Check if legacy data exists
        const legacyData = localStorage.getItem(STORAGE_KEYS.LEGACY);
        return legacyData !== null;

      } catch (error) {
        console.error('[ProjectListManager] Error checking migration:', error);
        return false;
      }
    }

    /**
     * Migrate from legacy wishlist format to new project list format
     * @returns {Object} { success: boolean, productCount: number, error: string|null }
     */
    migrateFromLegacy() {
      try {
        // Get legacy data
        const legacyRaw = localStorage.getItem(STORAGE_KEYS.LEGACY);
        if (!legacyRaw) {
          return { success: false, productCount: 0, error: 'No legacy data found' };
        }

        // Parse legacy data
        let legacyProducts = [];
        try {
          const parsed = JSON.parse(legacyRaw);
          if (!Array.isArray(parsed)) {
            console.warn('[ProjectListManager] Legacy data is not an array');
          } else {
            // Filter to only valid string handles and deduplicate
            legacyProducts = [...new Set(
              parsed.filter(item => typeof item === 'string' && item.trim().length > 0)
            )];
          }
        } catch (parseError) {
          console.warn('[ProjectListManager] Legacy data parse error:', parseError);
          return { success: false, productCount: 0, error: 'Legacy data is corrupted' };
        }

        // Backup legacy data
        try {
          localStorage.setItem(STORAGE_KEYS.BACKUP, legacyRaw);
          console.log('[ProjectListManager] Legacy data backed up');
        } catch (backupError) {
          console.warn('[ProjectListManager] Could not backup legacy data:', backupError);
        }

        // Create new data structure
        const now = new Date().toISOString();
        const listId = this._generateId();
        const defaultName = window.projectListConfig?.defaultListName || 'My First Project';

        this.data = {
          version: SCHEMA.VERSION,
          schemaVersion: SCHEMA.VERSION_STRING,
          migratedAt: now,
          activeListId: listId,
          lastModified: now,
          lists: [
            {
              id: listId,
              name: defaultName,
              slug: this._generateSlug(defaultName),
              createdAt: now,
              updatedAt: now,
              isDefault: true,
              products: legacyProducts,
              metadata: { ...DEFAULT_METADATA }
            }
          ]
        };

        // Save new data
        if (!this._saveData()) {
          return { success: false, productCount: 0, error: 'Failed to save migrated data' };
        }

        // Dispatch migration event
        this._dispatchEvent('projectlist:migrated', {
          legacyProductCount: legacyProducts.length
        });

        // Warn about large migrations
        if (legacyProducts.length > LIMITS.MAX_PRODUCTS_PER_LIST) {
          console.warn(`[ProjectListManager] Large migration: ${legacyProducts.length} products. Consider organizing into multiple lists.`);
        }

        return {
          success: true,
          productCount: legacyProducts.length,
          error: null
        };

      } catch (error) {
        console.error('[ProjectListManager] Migration error:', error);
        return { success: false, productCount: 0, error: error.message };
      }
    }

    /**
     * Rollback to legacy format (emergency use only)
     * @returns {Object} { success: boolean, error: string|null }
     */
    rollback() {
      try {
        // Check for backup
        const backup = localStorage.getItem(STORAGE_KEYS.BACKUP);
        if (!backup) {
          return { success: false, error: 'No backup found for rollback' };
        }

        // Restore legacy data
        localStorage.setItem(STORAGE_KEYS.LEGACY, backup);

        // Remove v2 data
        localStorage.removeItem(STORAGE_KEYS.NEW);

        console.log('[ProjectListManager] Rollback completed');

        // Dispatch rollback event
        this._dispatchEvent('projectlist:rolled-back', {});

        return { success: true, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Rollback error:', error);
        return { success: false, error: error.message };
      }
    }

    /* ========================================================================
       LIST MANAGEMENT METHODS
       ======================================================================== */

    /**
     * Create a new project list
     * @param {string} name - Display name for the list
     * @param {boolean} isDefault - Whether this should be the default list (default: false)
     * @param {Object} options - Optional settings { setAsActive: boolean, metadata: object }
     * @returns {Object} { success: boolean, list: Object|null, error: string|null }
     */
    createList(name, isDefault = false, options = {}) {
      try {
        // Validate name
        const nameValidation = this._validateListName(name);
        if (!nameValidation.valid) {
          return { success: false, list: null, error: nameValidation.error };
        }

        // Check max lists limit
        if (this.data.lists.length >= LIMITS.MAX_LISTS) {
          return {
            success: false,
            list: null,
            error: `Maximum of ${LIMITS.MAX_LISTS} lists allowed`
          };
        }

        const now = new Date().toISOString();
        const trimmedName = name.trim();

        // If setting as default, unset other default lists
        if (isDefault) {
          this.data.lists.forEach(list => {
            list.isDefault = false;
          });
        }

        // Create new list
        const newList = {
          id: this._generateId(),
          name: trimmedName,
          slug: this._generateSlug(trimmedName),
          createdAt: now,
          updatedAt: now,
          isDefault: isDefault,
          products: [],
          metadata: options.metadata || { ...DEFAULT_METADATA }
        };

        this.data.lists.push(newList);

        // Set as active if requested
        if (options.setAsActive) {
          this.data.activeListId = newList.id;
        }

        this._saveData();

        // Dispatch event
        this._dispatchEvent('projectlist:created', { list: { ...newList } });

        return { success: true, list: { ...newList }, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Create list error:', error);
        return { success: false, list: null, error: error.message };
      }
    }

    /**
     * Delete a project list by ID
     * Cannot delete the default list unless another list becomes default
     * Cannot delete the last remaining list
     * @param {string} listId - The list ID to delete
     * @returns {Object} { success: boolean, error: string|null }
     */
    deleteList(listId) {
      try {
        const listIndex = this.data.lists.findIndex(l => l.id === listId);

        if (listIndex === -1) {
          return { success: false, error: 'List not found' };
        }

        const list = this.data.lists[listIndex];

        // Cannot delete the last list
        if (this.data.lists.length === 1) {
          return { success: false, error: 'Cannot delete the last remaining list' };
        }

        // If deleting the default list, make another list default
        if (list.isDefault) {
          const newDefault = this.data.lists.find(l => l.id !== listId);
          if (newDefault) {
            newDefault.isDefault = true;
            newDefault.updatedAt = new Date().toISOString();
          }
        }

        // If deleting the active list, set active to first available
        if (this.data.activeListId === listId) {
          const newActive = this.data.lists.find(l => l.id !== listId);
          if (newActive) {
            this.data.activeListId = newActive.id;
          }
        }

        // Remove the list
        this.data.lists.splice(listIndex, 1);

        this._saveData();

        // Dispatch event
        this._dispatchEvent('projectlist:deleted', { listId });

        return { success: true, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Delete list error:', error);
        return { success: false, error: error.message };
      }
    }

    /**
     * Rename a project list
     * @param {string} listId - The list ID to rename
     * @param {string} newName - New display name
     * @returns {Object} { success: boolean, list: Object|null, error: string|null }
     */
    renameList(listId, newName) {
      try {
        const list = this.data.lists.find(l => l.id === listId);

        if (!list) {
          return { success: false, list: null, error: 'List not found' };
        }

        // Validate name (exclude current list from duplicate check)
        const nameValidation = this._validateListName(newName, listId);
        if (!nameValidation.valid) {
          return { success: false, list: null, error: nameValidation.error };
        }

        const oldName = list.name;
        const trimmedName = newName.trim();

        list.name = trimmedName;
        list.slug = this._generateSlug(trimmedName);
        list.updatedAt = new Date().toISOString();

        this._saveData();

        // Dispatch event
        this._dispatchEvent('projectlist:renamed', {
          listId,
          oldName,
          newName: trimmedName
        });

        return { success: true, list: { ...list }, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Rename list error:', error);
        return { success: false, list: null, error: error.message };
      }
    }

    /**
     * Get a single list by ID
     * @param {string} listId - The list ID
     * @returns {Object|null} The list object or null if not found
     */
    getList(listId) {
      const list = this.data.lists.find(l => l.id === listId);
      return list ? { ...list } : null;
    }

    /**
     * Get all project lists
     * @returns {Object[]} Array of all lists (copies)
     */
    getAllLists() {
      return this.data.lists.map(list => ({ ...list }));
    }

    /**
     * Set the active list
     * @param {string} listId - The list ID to set as active
     * @returns {Object} { success: boolean, error: string|null }
     */
    setActiveList(listId) {
      try {
        const list = this.data.lists.find(l => l.id === listId);

        if (!list) {
          return { success: false, error: 'List not found' };
        }

        const previousListId = this.data.activeListId;
        this.data.activeListId = listId;

        this._saveData();

        // Dispatch event
        this._dispatchEvent('projectlist:active-changed', {
          listId,
          previousListId
        });

        return { success: true, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Set active list error:', error);
        return { success: false, error: error.message };
      }
    }

    /**
     * Get the currently active list
     * @returns {Object} The active list object
     */
    getActiveList() {
      const list = this.data.lists.find(l => l.id === this.data.activeListId);
      return list ? { ...list } : this.getDefaultList();
    }

    /**
     * Get the default list
     * @returns {Object} The default list object
     */
    getDefaultList() {
      const list = this.data.lists.find(l => l.isDefault === true);
      return list ? { ...list } : this.data.lists[0];
    }

    /**
     * Set a list as the default
     * @param {string} listId - The list ID to set as default
     * @returns {Object} { success: boolean, error: string|null }
     */
    setDefaultList(listId) {
      try {
        const list = this.data.lists.find(l => l.id === listId);

        if (!list) {
          return { success: false, error: 'List not found' };
        }

        // Unset other defaults
        this.data.lists.forEach(l => {
          l.isDefault = false;
        });

        list.isDefault = true;
        list.updatedAt = new Date().toISOString();

        this._saveData();

        // Dispatch event
        this._dispatchEvent('projectlist:default-changed', { listId });

        return { success: true, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Set default list error:', error);
        return { success: false, error: error.message };
      }
    }

    /* ========================================================================
       PRODUCT MANAGEMENT METHODS
       ======================================================================== */

    /**
     * Add a product to a list
     * @param {string} listId - Target list ID
     * @param {string} productHandle - Product handle to add
     * @returns {Object} { success: boolean, alreadyExists: boolean, error: string|null }
     */
    addToList(listId, productHandle) {
      try {
        const list = this.data.lists.find(l => l.id === listId);

        if (!list) {
          return { success: false, alreadyExists: false, error: 'List not found' };
        }

        // Validate handle
        const handleValidation = this._validateProductHandle(productHandle);
        if (!handleValidation.valid) {
          return { success: false, alreadyExists: false, error: handleValidation.error };
        }

        const trimmedHandle = productHandle.trim();

        // Check if already in list
        if (list.products.includes(trimmedHandle)) {
          return { success: true, alreadyExists: true, error: null };
        }

        // Check products limit
        if (list.products.length >= LIMITS.MAX_PRODUCTS_PER_LIST) {
          return {
            success: false,
            alreadyExists: false,
            error: `Maximum of ${LIMITS.MAX_PRODUCTS_PER_LIST} products per list allowed`
          };
        }

        list.products.push(trimmedHandle);
        list.updatedAt = new Date().toISOString();

        this._saveData();

        // Dispatch new event
        this._dispatchEvent('projectlist:product-added', {
          listId,
          productHandle: trimmedHandle
        });

        // Dispatch legacy event for backward compatibility (if active list)
        if (listId === this.data.activeListId) {
          this._dispatchLegacyEvent('add:wishlist-item', { handle: trimmedHandle });
        }

        return { success: true, alreadyExists: false, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Add to list error:', error);
        return { success: false, alreadyExists: false, error: error.message };
      }
    }

    /**
     * Remove a product from a list
     * @param {string} listId - Target list ID
     * @param {string} productHandle - Product handle to remove
     * @returns {Object} { success: boolean, wasInList: boolean, error: string|null }
     */
    removeFromList(listId, productHandle) {
      try {
        const list = this.data.lists.find(l => l.id === listId);

        if (!list) {
          return { success: false, wasInList: false, error: 'List not found' };
        }

        // Validate handle
        const handleValidation = this._validateProductHandle(productHandle);
        if (!handleValidation.valid) {
          return { success: false, wasInList: false, error: handleValidation.error };
        }

        const trimmedHandle = productHandle.trim();
        const index = list.products.indexOf(trimmedHandle);

        if (index === -1) {
          return { success: true, wasInList: false, error: null };
        }

        list.products.splice(index, 1);
        list.updatedAt = new Date().toISOString();

        this._saveData();

        // Dispatch new event
        this._dispatchEvent('projectlist:product-removed', {
          listId,
          productHandle: trimmedHandle
        });

        // Dispatch legacy event for backward compatibility (if active list)
        if (listId === this.data.activeListId) {
          this._dispatchLegacyEvent('remove:wishlist-item', {
            handle: trimmedHandle,
            productId: null // Not always available
          });
        }

        return { success: true, wasInList: true, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Remove from list error:', error);
        return { success: false, wasInList: false, error: error.message };
      }
    }

    /**
     * Check if a product is in a specific list
     * @param {string} listId - List ID to check
     * @param {string} productHandle - Product handle to check
     * @returns {boolean} True if product is in list
     */
    isInList(listId, productHandle) {
      const list = this.data.lists.find(l => l.id === listId);
      if (!list) return false;
      return list.products.includes(productHandle.trim());
    }

    /**
     * Check if a product is in any list
     * @param {string} productHandle - Product handle to check
     * @returns {boolean} True if product is in any list
     */
    isInAnyList(productHandle) {
      const trimmedHandle = productHandle.trim();
      return this.data.lists.some(list => list.products.includes(trimmedHandle));
    }

    /**
     * Get all lists containing a product
     * @param {string} productHandle - Product handle to check
     * @returns {Object[]} Array of list objects containing the product
     */
    getListsContaining(productHandle) {
      const trimmedHandle = productHandle.trim();
      return this.data.lists
        .filter(list => list.products.includes(trimmedHandle))
        .map(list => ({ ...list }));
    }

    /**
     * Move a product from one list to another
     * @param {string} productHandle - Product handle to move
     * @param {string} fromListId - Source list ID
     * @param {string} toListId - Destination list ID
     * @returns {Object} { success: boolean, error: string|null }
     */
    moveProduct(productHandle, fromListId, toListId) {
      try {
        // Capture qty from source BEFORE removing so we can carry it over
        const sourceQty = this.getQuantity(fromListId, productHandle);

        // Remove from source
        const removeResult = this.removeFromList(fromListId, productHandle);
        if (!removeResult.success) {
          return removeResult;
        }

        if (!removeResult.wasInList) {
          return { success: false, error: 'Product not found in source list' };
        }

        // Add to destination
        const addResult = this.addToList(toListId, productHandle);
        if (!addResult.success) {
          // Rollback: add back to source
          this.addToList(fromListId, productHandle);
          return addResult;
        }

        // Carry the source quantity onto the destination (only when meaningful;
        // qty=1 is the implicit default so we don't bother writing it)
        if (sourceQty > 1) {
          this.setQuantity(toListId, productHandle, sourceQty);
        }

        // Dispatch move event
        this._dispatchEvent('projectlist:product-moved', {
          productHandle: productHandle.trim(),
          fromListId,
          toListId
        });

        return { success: true, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Move product error:', error);
        return { success: false, error: error.message };
      }
    }

    /**
     * Copy a product to another list
     * @param {string} productHandle - Product handle to copy
     * @param {string} fromListId - Source list ID (for validation)
     * @param {string} toListId - Destination list ID
     * @returns {Object} { success: boolean, alreadyExists: boolean, error: string|null }
     */
    copyProduct(productHandle, fromListId, toListId) {
      try {
        // Verify product exists in source
        if (!this.isInList(fromListId, productHandle)) {
          return { success: false, alreadyExists: false, error: 'Product not found in source list' };
        }

        // Add to destination
        const addResult = this.addToList(toListId, productHandle);

        if (addResult.success && !addResult.alreadyExists) {
          // Dispatch copy event
          this._dispatchEvent('projectlist:product-copied', {
            productHandle: productHandle.trim(),
            fromListId,
            toListId
          });
        }

        return addResult;

      } catch (error) {
        console.error('[ProjectListManager] Copy product error:', error);
        return { success: false, alreadyExists: false, error: error.message };
      }
    }

    /* ========================================================================
       UTILITY METHODS
       ======================================================================== */

    /**
     * Get total product count across all lists (unique products)
     * Used for header count bubble
     * @returns {number} Total unique products
     */
    getTotalProductCount() {
      const allProducts = new Set();
      this.data.lists.forEach(list => {
        list.products.forEach(handle => allProducts.add(handle));
      });
      return allProducts.size;
    }

    /**
     * Get product count for a specific list
     * @param {string} listId - List ID
     * @returns {number} Product count
     */
    getListProductCount(listId) {
      const list = this.data.lists.find(l => l.id === listId);
      return list ? list.products.length : 0;
    }

    /**
     * Get products from the active list
     * Convenience method for backward compatibility
     * @returns {string[]} Array of product handles
     */
    getActiveListProducts() {
      const activeList = this.getActiveList();
      return activeList ? [...activeList.products] : [];
    }

    /**
     * Export list data for sharing
     * @param {string} listId - List ID to export
     * @returns {Object|null} Exportable data object or null if not found
     */
    exportList(listId) {
      const list = this.data.lists.find(l => l.id === listId);
      if (!list) return null;

      return {
        name: list.name,
        products: [...list.products],
        exportedAt: new Date().toISOString()
      };
    }

    /**
     * Get the full data object (for debugging/admin)
     * @returns {Object} Copy of the full data object
     */
    getData() {
      return JSON.parse(JSON.stringify(this.data));
    }

    /* ========================================================================
       QUANTITY MANAGEMENT METHODS
       ======================================================================== */

    /**
     * Set the quantity for a product in a list
     * @param {string} listId - List ID
     * @param {string} handle - Product handle
     * @param {number} quantity - Quantity (minimum 1)
     * @returns {Object} { success: boolean, error: string|null }
     */
    setQuantity(listId, handle, quantity) {
      try {
        const list = this.data.lists.find(l => l.id === listId);
        if (!list) {
          return { success: false, error: 'List not found' };
        }

        const trimmedHandle = handle.trim();
        if (!list.products.includes(trimmedHandle)) {
          return { success: false, error: 'Product not in list' };
        }

        // Ensure quantity is valid (minimum 1)
        const qty = Math.max(1, Math.floor(Number(quantity) || 1));

        // Initialize quantities object if it doesn't exist
        if (!list.quantities) {
          list.quantities = {};
        }

        // Set the quantity
        list.quantities[trimmedHandle] = qty;

        // Save data
        this._saveData();

        // Dispatch event
        this._dispatchEvent('projectlist:quantity-changed', {
          listId,
          handle: trimmedHandle,
          quantity: qty
        });

        return { success: true, error: null };

      } catch (error) {
        console.error('[ProjectListManager] Set quantity error:', error);
        return { success: false, error: error.message };
      }
    }

    /**
     * Get the quantity for a product in a list
     * @param {string} listId - List ID
     * @param {string} handle - Product handle
     * @returns {number} Quantity (default: 1)
     */
    getQuantity(listId, handle) {
      try {
        const list = this.data.lists.find(l => l.id === listId);
        if (!list) return 1;

        const trimmedHandle = handle.trim();
        if (!list.products.includes(trimmedHandle)) return 1;

        // Return quantity from quantities object or default to 1
        if (list.quantities && list.quantities[trimmedHandle]) {
          return list.quantities[trimmedHandle];
        }

        return 1;
      } catch (error) {
        console.error('[ProjectListManager] Get quantity error:', error);
        return 1;
      }
    }

    /**
     * Get all quantities for a list
     * @param {string} listId - List ID
     * @returns {Object} Object with handle:quantity pairs
     */
    getListQuantities(listId) {
      try {
        const list = this.data.lists.find(l => l.id === listId);
        if (!list) return {};

        // Return a copy of quantities or empty object
        return list.quantities ? { ...list.quantities } : {};
      } catch (error) {
        console.error('[ProjectListManager] Get list quantities error:', error);
        return {};
      }
    }

    /**
     * Get product data with quantities for export
     * @param {string} listId - List ID
     * @returns {Array} Array of { handle, quantity } objects
     */
    getProductsWithQuantities(listId) {
      try {
        const list = this.data.lists.find(l => l.id === listId);
        if (!list) return [];

        return list.products.map(handle => ({
          handle,
          quantity: this.getQuantity(listId, handle)
        }));
      } catch (error) {
        console.error('[ProjectListManager] Get products with quantities error:', error);
        return [];
      }
    }

    /* ========================================================================
       EVENT DISPATCHING
       ======================================================================== */

    /**
     * Dispatch a custom event
     * @private
     * @param {string} eventName - Event name
     * @param {Object} detail - Event detail payload
     */
    _dispatchEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        detail,
        bubbles: true
      });
      document.dispatchEvent(event);
    }

    /**
     * Dispatch a legacy wishlist event for backward compatibility
     * @private
     * @param {string} eventName - Legacy event name
     * @param {Object} detail - Event detail payload
     */
    _dispatchLegacyEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        detail,
        bubbles: true
      });
      document.dispatchEvent(event);
    }
  }

  /* --------------------------------------------------------------------------
     INITIALIZATION
     -------------------------------------------------------------------------- */

  // Create global instance on DOM ready
  function initProjectListManager() {
    if (window.projectListManager) {
      console.log('[ProjectListManager] Already initialized');
      return;
    }

    window.projectListManager = new ProjectListManager();
  }

  // Initialize based on document state
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProjectListManager);
  } else {
    initProjectListManager();
  }

  // Re-initialize after Shopify section reload (if needed)
  document.addEventListener('shopify:section:load', () => {
    if (!window.projectListManager) {
      initProjectListManager();
    }
  });

})();

/* End Project List Manager */

/* ============================================
   PROJECT LIST MODAL
   File: snippets/project-list-modal.liquid

   Modal component for selecting/creating project lists
   when adding products from product cards.

   Dependencies:
   - ProjectListManager (must be loaded before this)
   ============================================ */

(function() {
  'use strict';

  /**
   * ProjectListModal - Custom element for the project list modal
   * Handles opening/closing, list selection, creating new lists, and adding products
   */
  class ProjectListModal extends HTMLElement {

    /* ========================================================================
       CONSTRUCTOR & LIFECYCLE
       ======================================================================== */

    constructor() {
      super();

      // State
      this.isOpen = false;
      this.isLoading = false;
      this.currentView = 'selection'; // 'selection' | 'create' | 'empty' | 'success'
      this.productData = null;
      this.selectedLists = new Set();
      this.originalListIds = new Set(); // Lists that already contain the product
      this.triggerElement = null; // Element that opened the modal (for focus return)

      // Bind methods
      this._handleKeydown = this._handleKeydown.bind(this);
      this._handleOverlayClick = this._handleOverlayClick.bind(this);
    }

    connectedCallback() {
      this._cacheElements();
      this._bindEvents();
      console.log('[ProjectListModal] Initialized');
    }

    disconnectedCallback() {
      this._unbindEvents();
    }

    /* ========================================================================
       ELEMENT CACHING
       ======================================================================== */

    _cacheElements() {
      // Modal structure
      this.overlay = this.querySelector('[data-modal-overlay]');
      this.content = this.querySelector('.project-list-modal__content');
      this.body = this.querySelector('[data-modal-body]');
      this.footer = this.querySelector('[data-modal-footer]');

      // Close/cancel buttons
      this.closeBtn = this.querySelector('[data-modal-close]');
      this.cancelBtn = this.querySelector('[data-modal-cancel]');
      this.submitBtn = this.querySelector('[data-modal-submit]');

      // Product info
      this.productImage = this.querySelector('[data-product-image]');
      this.productTitle = this.querySelector('[data-product-title]');
      this.productVariant = this.querySelector('[data-product-variant]');

      // Error banner
      this.errorBanner = this.querySelector('[data-error-banner]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.errorDismiss = this.querySelector('[data-dismiss-error]');

      // Views
      this.selectionView = this.querySelector('[data-view="selection"]');
      this.createView = this.querySelector('[data-view="create"]');
      this.emptyState = this.querySelector('[data-empty-state]');
      this.successState = this.querySelector('[data-success-state]');

      // List elements
      this.listContainer = this.querySelector('[data-list-container]');
      this.listElement = this.querySelector('[data-lists]');
      this.listItemTemplate = document.getElementById('project-list-item-template');

      // Create view elements
      this.createTrigger = this.querySelector('[data-create-trigger]');
      this.backTrigger = this.querySelector('[data-back-trigger]');
      this.listNameInput = this.querySelector('[data-list-name-input]');
      this.firstListInput = this.querySelector('[data-first-list-input]');
      this.charRemaining = this.querySelector('[data-char-remaining]');

      // Button text
      this.btnText = this.querySelector('[data-btn-text]');
      this.btnSpinner = this.querySelector('[data-btn-spinner]');

      // Success view
      this.successMessage = this.querySelector('[data-success-message]');
      this.successListName = this.querySelector('[data-success-list-name]');
    }

    /* ========================================================================
       EVENT BINDING
       ======================================================================== */

    _bindEvents() {
      // Close buttons
      this.closeBtn?.addEventListener('click', () => this.close());
      this.cancelBtn?.addEventListener('click', () => this.close());

      // Overlay click
      this.overlay?.addEventListener('click', this._handleOverlayClick);

      // Submit button
      this.submitBtn?.addEventListener('click', () => this._handleSubmit());

      // Create new list trigger
      this.createTrigger?.addEventListener('click', () => this._showCreateView());

      // Back to lists trigger
      this.backTrigger?.addEventListener('click', () => this._showSelectionView());

      // List name input
      this.listNameInput?.addEventListener('input', (e) => this._handleInputChange(e));
      this.firstListInput?.addEventListener('input', (e) => this._handleInputChange(e));

      // Error dismiss
      this.errorDismiss?.addEventListener('click', () => this._hideError());

      // Listen for external events
      document.addEventListener('projectlist:open-modal', (e) => this.open(e.detail));
    }

    _unbindEvents() {
      this.overlay?.removeEventListener('click', this._handleOverlayClick);
      document.removeEventListener('keydown', this._handleKeydown);
    }

    /* ========================================================================
       PUBLIC API
       ======================================================================== */

    /**
     * Open the modal with product data
     * @param {Object} data - Product data
     * @param {string} data.productHandle - Product handle
     * @param {string} data.productTitle - Product display title
     * @param {string} [data.productImage] - Product image URL
     * @param {string} [data.variantId] - Selected variant ID
     * @param {string} [data.variantTitle] - Variant title
     * @param {HTMLElement} [data.triggerElement] - Element that triggered the modal
     */
    open(data) {
      if (this.isOpen) return;

      // Validate required data
      if (!data || !data.productHandle) {
        console.error('[ProjectListModal] Missing required productHandle');
        return;
      }

      // Store product data
      this.productData = data;
      this.triggerElement = data.triggerElement || document.activeElement;

      // Reset state
      this.selectedLists.clear();
      this.originalListIds.clear();
      this._hideError();

      // Populate product info
      this._populateProductInfo();

      // Populate lists
      this._populateLists();

      // Show modal
      this.isOpen = true;
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus trap
      document.addEventListener('keydown', this._handleKeydown);

      // Focus first interactive element
      requestAnimationFrame(() => {
        const firstFocusable = this.content.querySelector('button, [href], input, [tabindex]:not([tabindex="-1"])');
        firstFocusable?.focus();
      });

      // Dispatch event
      this._dispatchEvent('projectlist:modal-opened', { productHandle: data.productHandle });
    }

    /**
     * Close the modal
     * @param {boolean} [immediate=false] - Skip closing animation
     */
    close(immediate = false) {
      if (!this.isOpen) return;

      // Add closing class for animation
      if (!immediate) {
        this.classList.add('project-list-modal--closing');
      }

      const cleanup = () => {
        this.isOpen = false;
        this.setAttribute('aria-hidden', 'true');
        this.classList.remove('project-list-modal--closing');
        document.body.style.overflow = '';

        // Remove event listener
        document.removeEventListener('keydown', this._handleKeydown);

        // Reset to selection view
        this._showSelectionView(true);

        // Return focus to trigger element
        if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
          this.triggerElement.focus();
        }

        // Dispatch event
        this._dispatchEvent('projectlist:modal-closed', {});
      };

      if (immediate) {
        cleanup();
      } else {
        setTimeout(cleanup, 200); // Match CSS transition duration
      }
    }

    /* ========================================================================
       PRIVATE METHODS - UI POPULATION
       ======================================================================== */

    _populateProductInfo() {
      if (!this.productData) return;

      // Set product image
      if (this.productImage && this.productData.productImage) {
        this.productImage.src = this.productData.productImage;
        this.productImage.alt = this.productData.productTitle || '';
      }

      // Set product title
      if (this.productTitle) {
        this.productTitle.textContent = this.productData.productTitle || '';
      }

      // Set variant info
      if (this.productVariant) {
        this.productVariant.textContent = this.productData.variantTitle || '';
        this.productVariant.style.display = this.productData.variantTitle ? '' : 'none';
      }
    }

    _populateLists() {
      const manager = window.projectListManager;
      if (!manager) {
        console.error('[ProjectListModal] ProjectListManager not available');
        this._showEmptyState();
        return;
      }

      const lists = manager.getAllLists();
      const productHandle = this.productData?.productHandle;

      // Check if empty
      if (!lists || lists.length === 0) {
        this._showEmptyState();
        return;
      }

      // Clear existing list items
      this.listElement.innerHTML = '';

      // Track which lists already contain this product
      this.originalListIds.clear();
      this.selectedLists.clear();

      // Sort lists: recently updated first
      const sortedLists = [...lists].sort((a, b) => {
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      });

      // Create list items
      sortedLists.forEach(list => {
        const listItem = this._createListItem(list, productHandle);
        this.listElement.appendChild(listItem);

        // If product is already in this list, track it and pre-select
        if (productHandle && manager.isInList(list.id, productHandle)) {
          this.originalListIds.add(list.id);
          this.selectedLists.add(list.id);
        }
      });

      // Show selection view
      this._showSelectionView(true);
      this._updateSubmitButton();
    }

    _createListItem(list, productHandle) {
      if (!this.listItemTemplate) {
        console.error('[ProjectListModal] List item template not found');
        return document.createElement('li');
      }

      const clone = this.listItemTemplate.content.cloneNode(true);
      const listItem = clone.querySelector('.project-list-modal__list-item');
      const checkbox = clone.querySelector('.project-list-modal__checkbox');
      const nameEl = clone.querySelector('.project-list-modal__list-name');
      const countEl = clone.querySelector('.project-list-modal__list-count');
      const inListIndicator = clone.querySelector('[data-in-list-indicator]');
      const editBtn = clone.querySelector('[data-edit-list-btn]');
      const editInputWrapper = clone.querySelector('[data-edit-input-wrapper]');
      const editInput = clone.querySelector('[data-edit-input]');
      const editSaveBtn = clone.querySelector('[data-edit-save-btn]');
      const editCancelBtn = clone.querySelector('[data-edit-cancel-btn]');

      // Store list ID on the item for reference
      if (listItem) {
        listItem.dataset.listId = list.id;
      }

      // Check if product already in list
      const manager = window.projectListManager;
      const isInList = productHandle && manager?.isInList(list.id, productHandle);

      // Set values
      if (checkbox) {
        checkbox.value = list.id;
        checkbox.id = `project-list-checkbox-${list.id}`;

        // Pre-check if product is already in list
        if (isInList) {
          checkbox.checked = true;
        }

        // Bind change event
        checkbox.addEventListener('change', (e) => this._handleCheckboxChange(e, list.id));
      }

      if (nameEl) {
        nameEl.textContent = list.name;
      }

      if (countEl) {
        countEl.textContent = `(${list.products?.length || 0})`;
      }

      // Show "Product currently in list" indicator
      if (inListIndicator) {
        inListIndicator.classList.toggle('is-hidden', !isInList);
      }

      // Edit button functionality
      if (editBtn) {
        editBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._startEditingListName(listItem, list.id, list.name);
        });
      }

      // Edit input handlers
      if (editInput) {
        editInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this._saveListName(listItem, list.id);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            this._cancelEditingListName(listItem);
          }
        });

        editInput.addEventListener('blur', (e) => {
          // Delay to allow button clicks to register
          setTimeout(() => {
            if (listItem.classList.contains('is-editing') &&
                !editInputWrapper.contains(document.activeElement)) {
              this._cancelEditingListName(listItem);
            }
          }, 150);
        });
      }

      // Save button
      if (editSaveBtn) {
        editSaveBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._saveListName(listItem, list.id);
        });
      }

      // Cancel button
      if (editCancelBtn) {
        editCancelBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._cancelEditingListName(listItem);
        });
      }

      return listItem;
    }

    /**
     * Start editing a list name inline
     */
    _startEditingListName(listItem, listId, currentName) {
      const editInput = listItem.querySelector('[data-edit-input]');

      if (!editInput) return;

      // Set current name in input
      editInput.value = currentName;

      // Add editing class
      listItem.classList.add('is-editing');

      // Focus input and select all text
      requestAnimationFrame(() => {
        editInput.focus();
        editInput.select();
      });
    }

    /**
     * Save the edited list name
     */
    _saveListName(listItem, listId) {
      const editInput = listItem.querySelector('[data-edit-input]');
      const nameEl = listItem.querySelector('[data-list-name-display]');

      if (!editInput) return;

      const newName = editInput.value.trim();

      if (!newName) {
        this._cancelEditingListName(listItem);
        return;
      }

      const manager = window.projectListManager;
      if (!manager) {
        this._cancelEditingListName(listItem);
        return;
      }

      const result = manager.renameList(listId, newName);

      if (result.success) {
        // Update displayed name
        if (nameEl) {
          nameEl.textContent = newName;
        }
        // Exit editing mode
        listItem.classList.remove('is-editing');
      } else {
        // Show error (could enhance with a toast notification)
        console.error('[ProjectListModal] Failed to rename list:', result.error);
        // Flash input to indicate error
        editInput.classList.add('is-invalid');
        setTimeout(() => editInput.classList.remove('is-invalid'), 500);
      }
    }

    /**
     * Cancel editing and restore original name
     */
    _cancelEditingListName(listItem) {
      listItem.classList.remove('is-editing');
    }

    /* ========================================================================
       PRIVATE METHODS - VIEW MANAGEMENT
       ======================================================================== */

    _showSelectionView(skipAnimation = false) {
      this.currentView = 'selection';

      // Hide other views
      this.createView?.classList.add('is-hidden');
      this.emptyState?.classList.add('is-hidden');
      this.successState?.classList.add('is-hidden');

      // Show selection view
      this.selectionView?.classList.remove('is-hidden');

      // Show footer
      this.footer?.classList.remove('is-hidden');

      // Update button text
      this._updateButtonText();
      this._updateSubmitButton();

      // Clear input
      if (this.listNameInput) {
        this.listNameInput.value = '';
        this._updateCharCount('');
      }
    }

    _showCreateView() {
      this.currentView = 'create';

      // Hide other views
      this.selectionView?.classList.add('is-hidden');
      this.emptyState?.classList.add('is-hidden');
      this.successState?.classList.add('is-hidden');

      // Show create view
      this.createView?.classList.remove('is-hidden');

      // Show footer
      this.footer?.classList.remove('is-hidden');

      // Update button text
      this._updateButtonText();
      this._updateSubmitButton();

      // Focus input
      requestAnimationFrame(() => {
        this.listNameInput?.focus();
      });
    }

    _showEmptyState() {
      this.currentView = 'empty';

      // Hide other views
      this.selectionView?.classList.add('is-hidden');
      this.createView?.classList.add('is-hidden');
      this.successState?.classList.add('is-hidden');

      // Show empty state
      this.emptyState?.classList.remove('is-hidden');

      // Show footer
      this.footer?.classList.remove('is-hidden');

      // Update button text
      this._updateButtonText();
      this._updateSubmitButton();

      // Focus input
      requestAnimationFrame(() => {
        this.firstListInput?.focus();
      });
    }

    _showSuccessState(message, listNames) {
      this.currentView = 'success';

      // Hide other views
      this.selectionView?.classList.add('is-hidden');
      this.createView?.classList.add('is-hidden');
      this.emptyState?.classList.add('is-hidden');

      // Hide footer
      this.footer?.classList.add('is-hidden');

      // Show success state
      this.successState?.classList.remove('is-hidden');

      // Update success message
      if (this.successMessage) {
        this.successMessage.textContent = message || 'Added to List!';
      }

      if (this.successListName) {
        this.successListName.textContent = listNames || '';
      }

      // Auto-close after delay
      setTimeout(() => {
        this.close();
      }, 1500);
    }

    /* ========================================================================
       PRIVATE METHODS - EVENT HANDLERS
       ======================================================================== */

    _handleKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }

      // Focus trap
      if (e.key === 'Tab') {
        const focusableElements = this.content.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        const visibleFocusable = Array.from(focusableElements).filter(el => {
          return el.offsetParent !== null && !el.closest('.is-hidden');
        });

        if (visibleFocusable.length === 0) return;

        const firstFocusable = visibleFocusable[0];
        const lastFocusable = visibleFocusable[visibleFocusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    }

    _handleOverlayClick(e) {
      if (e.target === this.overlay) {
        this.close();
      }
    }

    _handleCheckboxChange(e, listId) {
      if (e.target.checked) {
        this.selectedLists.add(listId);
      } else {
        this.selectedLists.delete(listId);
      }
      this._updateSubmitButton();
    }

    _handleInputChange(e) {
      const value = e.target.value;
      this._updateCharCount(value);
      this._updateSubmitButton();
    }

    _updateCharCount(value) {
      const maxLength = 50;
      const remaining = maxLength - (value?.length || 0);

      if (this.charRemaining) {
        this.charRemaining.textContent = remaining;
      }
    }

    _updateButtonText() {
      if (!this.btnText) return;

      const textMap = {
        selection: this.originalListIds.size > 0 ? 'Update Lists' : 'Add to List',
        create: 'Create & Add',
        empty: 'Create & Add'
      };

      this.btnText.textContent = textMap[this.currentView] || 'Add to List';
    }

    _updateSubmitButton() {
      if (!this.submitBtn) return;

      let isValid = false;

      if (this.currentView === 'selection') {
        // Valid when:
        //  - selection differs from original membership (add or remove), OR
        //  - a quantity was passed in (from the product page) and at least one list is checked
        //    so we can stack that quantity onto the existing entries.
        const hasAdditions = [...this.selectedLists].some(id => !this.originalListIds.has(id));
        const hasRemovals = [...this.originalListIds].some(id => !this.selectedLists.has(id));
        const hasQuantityToApply =
          this._normalizeQuantity(this.productData?.quantity) !== null && this.selectedLists.size > 0;
        isValid = hasAdditions || hasRemovals || hasQuantityToApply;
      } else if (this.currentView === 'create') {
        // Valid if input has value
        const inputValue = this.listNameInput?.value?.trim();
        isValid = inputValue && inputValue.length > 0 && inputValue.length <= 50;
      } else if (this.currentView === 'empty') {
        // Valid if first list input has value
        const inputValue = this.firstListInput?.value?.trim();
        isValid = inputValue && inputValue.length > 0 && inputValue.length <= 50;
      }

      this.submitBtn.disabled = !isValid;
    }

    /* ========================================================================
       PRIVATE METHODS - SUBMIT HANDLERS
       ======================================================================== */

    _normalizeQuantity(value) {
      if (value === undefined || value === null || value === '') return null;
      const parsed = Math.floor(Number(value));
      if (!Number.isFinite(parsed) || parsed < 1) return null;
      return parsed;
    }

    async _handleSubmit() {
      if (this.isLoading) return;

      if (this.currentView === 'selection') {
        await this._handleSelectionSubmit();
      } else if (this.currentView === 'create') {
        await this._handleCreateSubmit();
      } else if (this.currentView === 'empty') {
        await this._handleEmptySubmit();
      }
    }

    async _handleSelectionSubmit() {
      const manager = window.projectListManager;
      if (!manager || !this.productData?.productHandle) {
        this._showError('Unable to add product. Please try again.');
        return;
      }

      const productHandle = this.productData.productHandle;
      const submittedQuantity = this._normalizeQuantity(this.productData.quantity);
      const addedListNames = [];
      const removedListNames = [];
      let hasErrors = false;

      this._setLoading(true);

      try {
        // Determine which lists to add to and remove from
        const listsToAdd = [...this.selectedLists].filter(id => !this.originalListIds.has(id));
        const listsToRemove = [...this.originalListIds].filter(id => !this.selectedLists.has(id));

        // Capture qty from any list being removed BEFORE we remove it, so a
        // "move" (uncheck one list + check another in the same submit) carries
        // the saved quantity to the destination. Max wins when multiple sources.
        let movedQty = 0;
        for (const listId of listsToRemove) {
          const qty = manager.getQuantity(listId, productHandle);
          if (qty > movedQty) movedQty = qty;
        }

        // Add to new lists
        for (const listId of listsToAdd) {
          const result = manager.addToList(listId, productHandle);
          if (result.success) {
            const list = manager.getList(listId);
            if (list) addedListNames.push(list.name);
          } else {
            hasErrors = true;
            console.error('[ProjectListModal] Failed to add to list:', result.error);
          }
        }

        // Remove from unchecked lists
        for (const listId of listsToRemove) {
          const result = manager.removeFromList(listId, productHandle);
          if (result.success) {
            const list = manager.getList(listId);
            if (list) removedListNames.push(list.name);
          } else {
            hasErrors = true;
            console.error('[ProjectListModal] Failed to remove from list:', result.error);
          }
        }

        // Carry the moved qty to every newly-added list so the saved quantity
        // survives a move from one list to another.
        if (movedQty > 0) {
          for (const listId of listsToAdd) {
            if (this.selectedLists.has(listId)) {
              manager.setQuantity(listId, productHandle, movedQty);
            }
          }
        }

        // Persist quantity (from product page) on every list still checked.
        // Stacks on top of the existing/moved qty (capped at 9999).
        const stackedQtyListNames = [];
        if (submittedQuantity !== null) {
          for (const listId of this.selectedLists) {
            const wasAlreadyInList = this.originalListIds.has(listId);
            const wasMoveTarget = !wasAlreadyInList && movedQty > 0;
            const existingQty = (wasAlreadyInList || wasMoveTarget)
              ? manager.getQuantity(listId, productHandle)
              : 0;
            const nextQty = Math.min(9999, existingQty + submittedQuantity);
            manager.setQuantity(listId, productHandle, nextQty);
            if (wasAlreadyInList) {
              const list = manager.getList(listId);
              if (list) stackedQtyListNames.push(list.name);
            }
          }
        }

        if (hasErrors) {
          this._showError('Some changes could not be saved. Please try again.');
          this._setLoading(false);
          return;
        }

        // Build success message
        let message = '';
        let listNamesText = '';

        if (addedListNames.length > 0) {
          message = addedListNames.length === 1 ? 'Added to List!' : `Added to ${addedListNames.length} Lists!`;
          listNamesText = addedListNames.join(', ');
        } else if (stackedQtyListNames.length > 0) {
          message = `Quantity Updated (+${submittedQuantity})`;
          listNamesText = stackedQtyListNames.join(', ');
        } else if (removedListNames.length > 0) {
          message = 'Lists Updated!';
          listNamesText = `Removed from: ${removedListNames.join(', ')}`;
        } else {
          message = 'No Changes';
        }

        // Dispatch event for UI updates
        this._dispatchEvent('projectlist:product-updated', {
          productHandle,
          addedTo: addedListNames,
          removedFrom: removedListNames
        });

        this._setLoading(false);
        this._showSuccessState(message, listNamesText);

      } catch (error) {
        console.error('[ProjectListModal] Submit error:', error);
        this._showError('An error occurred. Please try again.');
        this._setLoading(false);
      }
    }

    async _handleCreateSubmit() {
      const manager = window.projectListManager;
      const listName = this.listNameInput?.value?.trim();

      if (!manager || !listName || !this.productData?.productHandle) {
        this._showError('Please enter a list name.');
        return;
      }

      this._setLoading(true);

      try {
        // Create the list
        const createResult = manager.createList(listName, false, { setAsActive: false });

        if (!createResult.success) {
          this._showError(createResult.error || 'Failed to create list.');
          this._setLoading(false);
          return;
        }

        // Add product to the new list
        const addResult = manager.addToList(createResult.list.id, this.productData.productHandle);

        if (!addResult.success) {
          this._showError(addResult.error || 'Failed to add product to list.');
          this._setLoading(false);
          return;
        }

        // Persist quantity (from product page) on the new list
        const submittedQuantity = this._normalizeQuantity(this.productData.quantity);
        if (submittedQuantity !== null) {
          manager.setQuantity(createResult.list.id, this.productData.productHandle, submittedQuantity);
        }

        // Dispatch event
        this._dispatchEvent('projectlist:product-updated', {
          productHandle: this.productData.productHandle,
          addedTo: [listName],
          removedFrom: [],
          newListCreated: true
        });

        this._setLoading(false);
        this._showSuccessState('Created & Added!', `"${listName}"`);

      } catch (error) {
        console.error('[ProjectListModal] Create error:', error);
        this._showError('An error occurred. Please try again.');
        this._setLoading(false);
      }
    }

    async _handleEmptySubmit() {
      const manager = window.projectListManager;
      const listName = this.firstListInput?.value?.trim();

      if (!manager || !listName || !this.productData?.productHandle) {
        this._showError('Please enter a list name.');
        return;
      }

      this._setLoading(true);

      try {
        // Create the first list (set as default)
        const createResult = manager.createList(listName, true, { setAsActive: true });

        if (!createResult.success) {
          this._showError(createResult.error || 'Failed to create list.');
          this._setLoading(false);
          return;
        }

        // Add product to the new list
        const addResult = manager.addToList(createResult.list.id, this.productData.productHandle);

        if (!addResult.success) {
          this._showError(addResult.error || 'Failed to add product to list.');
          this._setLoading(false);
          return;
        }

        // Persist quantity (from product page) on the new list
        const submittedQuantity = this._normalizeQuantity(this.productData.quantity);
        if (submittedQuantity !== null) {
          manager.setQuantity(createResult.list.id, this.productData.productHandle, submittedQuantity);
        }

        // Dispatch event
        this._dispatchEvent('projectlist:product-updated', {
          productHandle: this.productData.productHandle,
          addedTo: [listName],
          removedFrom: [],
          newListCreated: true
        });

        this._setLoading(false);
        this._showSuccessState('Created & Added!', `"${listName}"`);

      } catch (error) {
        console.error('[ProjectListModal] Empty state error:', error);
        this._showError('An error occurred. Please try again.');
        this._setLoading(false);
      }
    }

    /* ========================================================================
       PRIVATE METHODS - UI STATE
       ======================================================================== */

    _setLoading(loading) {
      this.isLoading = loading;

      // Toggle loading class
      this.classList.toggle('project-list-modal--loading', loading);

      // Update submit button
      if (this.submitBtn) {
        this.submitBtn.classList.toggle('is-loading', loading);
        this.submitBtn.disabled = loading;
      }
    }

    _showError(message) {
      if (this.errorBanner && this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorBanner.classList.remove('is-hidden');

        // Announce to screen readers
        this.errorBanner.setAttribute('role', 'alert');
      }
    }

    _hideError() {
      if (this.errorBanner) {
        this.errorBanner.classList.add('is-hidden');
      }
    }

    /* ========================================================================
       PRIVATE METHODS - EVENTS
       ======================================================================== */

    _dispatchEvent(eventName, detail) {
      const event = new CustomEvent(eventName, {
        detail,
        bubbles: true
      });
      this.dispatchEvent(event);
      document.dispatchEvent(event);
    }
  }

  // Register the custom element
  if (!customElements.get('project-list-modal')) {
    customElements.define('project-list-modal', ProjectListModal);
  }

  // Expose to window for debugging
  window.ProjectListModal = ProjectListModal;

})();

/* End Project List Modal */

/* ============================================
   MEGA MENU SCROLL INDICATOR
   File: snippets/header-menu-vertical.liquid

   Shows a "Scroll for more" indicator at bottom-right
   of mega menus when content is scrollable.
   Hides after user scrolls down.

   The indicator is created dynamically via JS to avoid
   issues with the Liquid template structure.
   ============================================ */

(function() {
  'use strict';

  // Single shared indicator element (reused across menus)
  let scrollIndicator = null;
  let currentContainer = null;
  let showTimeout = null; // Track pending show timeout

  /**
   * Create the scroll indicator element
   * Design: Red ball with arrow + grey text container
   */
  function createScrollIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'mega-menu__scroll-indicator';
    indicator.innerHTML = `
      <div class="mega-menu__scroll-indicator-ball">
        <span class="mega-menu__scroll-indicator-arrow">↓</span>
      </div>
      <div class="mega-menu__scroll-indicator-text-container">
        <span class="mega-menu__scroll-indicator-text">Scroll for more</span>
      </div>
    `;
    document.body.appendChild(indicator);
    return indicator;
  }

  /**
   * Initialize scroll indicators for mega menus
   */
  function initMegaMenuScrollIndicators() {
    const verticalMenu = document.querySelector('.header__vertical-menu');
    if (!verticalMenu) return;

    // Create the shared indicator element
    scrollIndicator = createScrollIndicator();

    // Use MutationObserver to detect when mega menus open
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const details = mutation.target;
          if (!details.classList.contains('mega-menu')) return;

          const isOpen = details.getAttribute('open') === 'true';

          if (isOpen) {
            // Mega menu just opened
            const megaMenuContent = details.querySelector('.mega-menu__content');
            if (megaMenuContent) {
              showScrollIndicator(megaMenuContent, details);
            }
          } else {
            // Mega menu closed (open="false" or attribute removed)
            const closedContent = details.querySelector('.mega-menu__content');
            if (closedContent === currentContainer) {
              resetScrollIndicator();
            }
          }
        }
      });
    });

    // Observe all mega menu details elements
    const megaMenus = verticalMenu.querySelectorAll('details.mega-menu');
    megaMenus.forEach((menu) => {
      observer.observe(menu, { attributes: true });
    });
  }

  /**
   * Show scroll indicator for a specific mega menu if scrollable
   * @param {HTMLElement} container - The mega menu content container
   * @param {HTMLElement} detailsEl - The parent details element
   */
  function showScrollIndicator(container, detailsEl) {
    // Cancel any pending show timeout (user switched menus quickly)
    if (showTimeout) {
      clearTimeout(showTimeout);
      showTimeout = null;
    }

    // Hide indicator immediately when switching menus
    hideScrollIndicator();

    // Update currentContainer right away so scroll events work
    currentContainer = container;

    // Add scroll listener if not already attached
    if (!container.dataset.scrollListenerAttached) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      container.dataset.scrollListenerAttached = 'true';
    }

    // 1 second delay before showing the indicator
    showTimeout = setTimeout(() => {
      // Check if this container is still the current one (user didn't switch menus)
      if (container !== currentContainer) return;

      // Re-verify the menu is still actually open
      if (detailsEl && detailsEl.getAttribute('open') !== 'true') return;

      // Check if content is scrollable
      const isScrollable = container.scrollHeight > container.clientHeight;

      if (isScrollable && container.scrollTop < 20) {
        positionIndicator(container);
        scrollIndicator.classList.add('is-visible');
      }
    }, 1000);
  }

  /**
   * Position the scroll indicator at bottom-right of mega menu
   * @param {HTMLElement} container - The mega menu content container
   */
  function positionIndicator(container) {
    if (!scrollIndicator || !container) return;

    const rect = container.getBoundingClientRect();
    // Position 12px from bottom-right corner of the mega menu
    scrollIndicator.style.bottom = (window.innerHeight - rect.bottom + 12) + 'px';
    scrollIndicator.style.right = (window.innerWidth - rect.right + 12) + 'px';
  }

  /**
   * Hide the scroll indicator (visually only)
   */
  function hideScrollIndicator() {
    if (scrollIndicator) {
      scrollIndicator.classList.remove('is-visible');
    }
  }

  /**
   * Fully reset the scroll indicator (when menu closes)
   */
  function resetScrollIndicator() {
    // Cancel any pending show timeout
    if (showTimeout) {
      clearTimeout(showTimeout);
      showTimeout = null;
    }
    hideScrollIndicator();
    currentContainer = null;
  }

  /**
   * Handle scroll event on mega menu
   * @param {Event} e - Scroll event
   */
  function handleScroll(e) {
    const container = e.target;
    // Hide indicator after scrolling more than 20px
    if (container.scrollTop > 20) {
      hideScrollIndicator();
    } else if (container.scrollTop < 20 && container === currentContainer) {
      // Show again if scrolled back to top
      positionIndicator(container);
      scrollIndicator.classList.add('is-visible');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaMenuScrollIndicators);
  } else {
    initMegaMenuScrollIndicators();
  }

})();

/* End Mega Menu Scroll Indicator */

/* ============================================================
   SECTION: main-collection-grouped.liquid
   SNIPPET: grouped-product-row.liquid
   ProductGrouperV2 - Builds option matrix UI from variant titles.
   Each product group's variant data is embedded as JSON by Liquid.
   JS derives option groups from title segments at render time,
   builds interactive option rows (Color circles, Length pills,
   etc.) and handles selection matching, DOM updates, qty buttons,
   add-to-cart.
   ============================================================ */

/* ── Shared Helpers: Option Derivation ───────────────────── */

/**
 * Natural sort comparator — numbers sort numerically, text alphabetically.
 * "1ft" < "4ft" < "10ft" < "25ft" (not lexicographic "10ft" < "1ft")
 */
function naturalSort(a, b) {
  const numA = parseFloat(a);
  const numB = parseFloat(b);
  if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
  if (!isNaN(numA)) return -1;
  if (!isNaN(numB)) return 1;
  return a.localeCompare(b);
}

/**
 * Derive option groups from variant titles instead of stored metaobject options.
 * Splits titles by " - ", finds common prefix segments, then collects unique
 * values at each varying position. Detects color groups via colorMap lookup.
 *
 * @param {Array} variants - Array of variant objects with .title and .colors
 * @param {Object} colorMap - Map of color name → hex value
 * @returns {Array} Array of { name, position, values } option groups
 */
function deriveOptionsFromTitles(variants, colorMap) {
  if (!variants || variants.length === 0) return [];

  const allParts = variants.map(v => v.title.split(' - '));
  const minLen = Math.min(...allParts.map(p => p.length));
  const maxLen = Math.max(...allParts.map(p => p.length));

  // Find common prefix length (segments identical across ALL variants)
  let prefixLen = 0;
  for (let i = 0; i < minLen; i++) {
    const vals = new Set(allParts.map(p => p[i]));
    if (vals.size === 1) prefixLen = i + 1;
    else break;
  }

  const options = [];
  let pos = 0;
  for (let i = prefixLen; i < maxLen; i++) {
    const vals = [...new Set(allParts.map(p => p[i]).filter(Boolean))];
    if (vals.length <= 1) continue;

    // Detect if this position is a color group
    const colorMatchCount = vals.filter(v => colorMap[v]).length;
    const isColor = colorMatchCount > 0 && (colorMatchCount / vals.length) > 0.3;

    // Sort: colors alphabetically, others with natural sort
    vals.sort(isColor ? (a, b) => a.localeCompare(b) : naturalSort);

    options.push({
      name: isColor ? 'Color' : 'Option',
      position: pos,
      values: vals
    });
    pos++;
  }

  return options;
}

class ProductGrouperV2 {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.sectionId = sectionEl.dataset.sectionId;
    this.init();
  }

  init() {
    this.forceListView();
    this.buildAllOptionUIs();
    this.bindSwatchClicks();
    this.bindQuantityButtons();
    this.bindAddToCart();
    this.setupFilterListener();
    this.fetchAndCorrectGroups();
  }

  forceListView() {
    const grid = this.section.querySelector('.product-grid');
    if (grid) {
      grid.setAttribute('data-view', '1');
      grid.setAttribute('product-grid-view', 'default');
    }
    const device = window.innerWidth >= 750 ? 'desktop' : 'mobile';
    try { sessionStorage.setItem('product-grid-view-' + device, '1'); } catch (e) {}
  }

  /* ── Option Matrix Builder ─────────────────────────────── */

  buildAllOptionUIs() {
    this.section.querySelectorAll('[data-options-ui]').forEach(container => {
      const row = container.closest('.gprow');
      if (!row) return;
      const variantsScript = row.querySelector('[data-group-variants]');
      if (!variantsScript) return;

      try {
        const variants = JSON.parse(variantsScript.textContent);
        const currentProductId = container.dataset.currentProductId;
        if (variants.length > 0) {
          this.buildOptionUI(container, variants, currentProductId);
        }
      } catch (e) {
        console.warn('ProductGrouperV2: Failed to parse group data', e);
      }
    });
  }

  getCommonPrefixLength(variants) {
    if (variants.length < 2) return 0;
    const allParts = variants.map(v => v.title.split(' - '));
    const minLen = Math.min(...allParts.map(p => p.length));
    let common = 0;
    for (let i = 0; i < minLen; i++) {
      const vals = new Set(allParts.map(p => p[i]));
      if (vals.size === 1) common = i + 1;
      else break;
    }
    return common;
  }

  buildColorMap(variants) {
    const colorMap = {};
    variants.forEach(v => {
      if (v.colors && typeof v.colors === 'object') {
        Object.entries(v.colors).forEach(([name, hex]) => {
          colorMap[name] = hex;
        });
      }
    });
    return colorMap;
  }

  buildOptionUI(container, variants, currentProductId) {
    const colorMap = this.buildColorMap(variants);
    const options = deriveOptionsFromTitles(variants, colorMap);
    if (options.length === 0) return;

    const prefixLen = this.getCommonPrefixLength(variants);

    // Determine the current variant's option values for initial selection
    const currentVariant = variants.find(v => String(v.productId) === String(currentProductId)) || variants[0];
    const currentParts = currentVariant.title.split(' - ');
    const currentOptions = currentParts.slice(prefixLen);

    let html = '';

    options.forEach((optionGroup) => {
      const isColor = optionGroup.name === 'Color';
      const groupLabel = optionGroup.name;
      const selectedValue = currentOptions[optionGroup.position] || '';

      html += '<div class="gprow__option-group" data-option-name="' + groupLabel + '" data-option-position="' + optionGroup.position + '">';
      html += '<span class="gprow__option-label">' + groupLabel + ':</span>';
      html += '<div class="gprow__option-values">';

      optionGroup.values.forEach(val => {
        const isSelected = val === selectedValue;
        const classes = ['gprow__swatch'];

        // For color groups, render each value as a circle if it has a hex, pill otherwise
        const hex = colorMap[val] || '';
        if (isColor && hex) {
          classes.push('gprow__swatch--color');
        } else {
          classes.push('gprow__swatch--pill');
        }

        if (isSelected) classes.push('gprow__swatch--selected');

        html += '<button type="button" class="' + classes.join(' ') + '"';
        if (hex) html += ' style="background-color: ' + hex + ';"';
        html += ' data-option-value="' + this.escAttr(val) + '"';
        html += ' data-option-position="' + optionGroup.position + '"';
        html += ' aria-label="' + this.escAttr(val) + '"';
        if (isColor && hex) html += ' data-color-name="' + this.escAttr(val) + '"';
        html += '>';
        if (!(isColor && hex)) html += this.escHtml(val);
        html += '</button>';
      });

      html += '</div></div>';
    });

    container.innerHTML = html;

    // Store parsed data on the row for click handler
    const row = container.closest('.gprow');
    if (row) {
      row._groupVariants = variants;
      row._groupOptions = options;
      row._prefixLen = prefixLen;

      // Hide option groups irrelevant to the initially selected variant
      const currentVariant = variants.find(v => String(v.productId) === String(currentProductId)) || variants[0];
      if (currentVariant) {
        this.updateGroupVisibility(row, currentVariant);
      }
    }
  }

  escAttr(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ── Swatch Click Handling ─────────────────────────────── */

  bindSwatchClicks() {
    this.section.addEventListener('click', (e) => {
      const swatch = e.target.closest('.gprow__swatch');
      if (!swatch) return;
      e.preventDefault();
      e.stopPropagation();
      const row = swatch.closest('.gprow');
      if (!row) return;
      try {
        this.handleSwatchClick(swatch, row);
      } catch (err) {
        console.error('ProductGrouperV2 handleSwatchClick error:', err);
      }
    });
  }

  handleSwatchClick(swatch, row) {
    // Update selection in this option group
    const group = swatch.closest('.gprow__option-group');
    if (group) {
      group.querySelectorAll('.gprow__swatch').forEach(s => s.classList.remove('gprow__swatch--selected'));
      swatch.classList.add('gprow__swatch--selected');
    }

    // Pass the clicked group's position so findBestVariant prioritizes the user's choice
    const clickedPos = group ? group.dataset.optionPosition : null;

    // Find the best matching variant and sync all other groups to it
    if (row._groupVariants && row._groupOptions) {
      const match = this.findBestVariant(row, clickedPos);
      if (match) {
        this.syncSelectionsToVariant(row, match);
        this.updateGroupVisibility(row, match);
        this.updateRowDisplay(row, match);
      }
    }
  }

  /* ── Sync UI selections to match a variant's title ──── */

  syncSelectionsToVariant(row, variant) {
    const options = row._groupOptions;
    const prefixLen = row._prefixLen || 0;
    if (!options || !variant.title) return;

    const parts = variant.title.split(' - ');
    row.querySelectorAll('.gprow__option-group').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition, 10);
      const expectedVal = parts[prefixLen + pos];
      if (!expectedVal) return;

      // Check if the correct value is already selected
      const currentSelected = group.querySelector('.gprow__swatch--selected');
      if (currentSelected && currentSelected.dataset.optionValue === expectedVal) return;

      // Find and select the correct swatch
      const correctSwatch = group.querySelector('[data-option-value="' + CSS.escape(expectedVal) + '"]');
      if (correctSwatch) {
        group.querySelectorAll('.gprow__swatch').forEach(s => s.classList.remove('gprow__swatch--selected'));
        correctSwatch.classList.add('gprow__swatch--selected');
      }
    });
  }

  /* ── Show/Hide option groups based on selected variant branch ── */

  updateGroupVisibility(row, variant) {
    const variants = row._groupVariants;
    const options = row._groupOptions;
    const prefixLen = row._prefixLen || 0;
    if (!variants || !options || options.length <= 1) return;

    const variantParts = variant.title.split(' - ');
    const variantSegCount = variantParts.length;

    // Find all variants with the same segment count (same structural "branch").
    // E.g. WAVENET variants have 2 segments, DATACOMM variants have 3 segments.
    // This groups them by structure rather than exact pos0 value, so all DATACOMM
    // colors remain visible when switching between DATACOMM SKUs.
    const branchVariants = variants.filter(v => {
      return v.title.split(' - ').length === variantSegCount;
    });

    // For each option group (skip pos 0 — the branch selector itself)
    row.querySelectorAll('.gprow__option-group').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition, 10);

      // Always show the first option group (branch selector)
      if (pos === 0) {
        group.style.display = '';
        return;
      }

      // Collect values that exist at this position in branch variants
      const validValues = new Set();
      branchVariants.forEach(v => {
        const p = v.title.split(' - ');
        const val = p[prefixLen + pos];
        if (val) validValues.add(val);
      });

      // Show/hide individual swatches
      let visibleCount = 0;
      group.querySelectorAll('.gprow__swatch').forEach(swatch => {
        const val = swatch.dataset.optionValue;
        if (validValues.has(val)) {
          swatch.style.display = '';
          visibleCount++;
        } else {
          swatch.style.display = 'none';
          swatch.classList.remove('gprow__swatch--selected');
        }
      });

      // Hide entire group if 0 or 1 visible swatches (nothing to choose)
      if (visibleCount <= 1) {
        group.style.display = 'none';
      } else {
        group.style.display = '';
      }
    });
  }

  /* ── Find Best Matching Variant (Bidirectional) ──────── */

  findBestVariant(row, clickedPos) {
    const variants = row._groupVariants;
    const options = row._groupOptions;
    const prefixLen = row._prefixLen || 0;
    if (!variants || !options) return null;

    // Collect current selections from all option groups
    const selections = {};
    row.querySelectorAll('.gprow__option-group').forEach(group => {
      const pos = group.dataset.optionPosition;
      const selected = group.querySelector('.gprow__swatch--selected');
      if (selected) {
        selections[pos] = selected.dataset.optionValue;
      }
    });

    // Score each variant: count how many current selections it matches.
    // The clicked group's match gets heavy weight (10) so user intent always wins tie-breaks.
    // Other groups get weight 1. Available variants get +100 boost.
    let best = null;
    let bestScore = -1;
    variants.forEach(v => {
      const parts = v.title.split(' - ');
      let score = 0;
      options.forEach(opt => {
        const variantVal = parts[prefixLen + opt.position];
        const selectedVal = selections[opt.position];
        if (selectedVal && variantVal === selectedVal) {
          score += (clickedPos != null && String(opt.position) === String(clickedPos)) ? 10 : 1;
        }
      });
      // Boost available variants so they're preferred over unavailable ones
      if (v.available) score += 100;
      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    });

    return best;
  }

  /* ── Row Display Updates ─────────────────────────────── */

  updateRowDisplay(row, data) {
    // Image
    if (data.image) {
      const img = row.querySelector('[data-gprow-img]');
      if (img) {
        img.style.opacity = '0';
        setTimeout(() => {
          img.src = data.imageLarge || data.image;
          img.alt = data.imageAlt || data.title;
          img.style.opacity = '1';
        }, 150);
      }
    }

    // Title
    const titleLink = row.querySelector('[data-gprow-title-link]');
    if (titleLink) {
      titleLink.textContent = data.title;
      titleLink.href = data.url;
    }

    // Description
    const descEl = row.querySelector('[data-gprow-description]');
    if (descEl) {
      if (data.description) {
        descEl.textContent = data.description;
        descEl.style.display = '';
      } else {
        descEl.textContent = '';
        descEl.style.display = 'none';
      }
    }

    const priceWrap = row.querySelector('[data-gprow-price-wrap]');
    if (priceWrap) {
      priceWrap.replaceChildren();
      const amount = document.createElement('span');
      if (data.displayPrice === 0) {
        amount.className = 'gprow__call-pricing';
        amount.setAttribute('data-gprow-call-pricing', '');
        amount.textContent = 'Call For Updated Pricing';
      } else {
        if (data.dealerPrice) {
          const label = document.createElement('span');
          label.className = 'gprow__price-label';
          label.textContent = 'Dealer';
          priceWrap.append(label);
        }
        if (data.salePercent > 0) {
          const original = document.createElement('s');
          original.className = 'cp-sale-display-original';
          original.setAttribute('data-cp-sale-original', '');
          original.textContent = data.originalPriceFormatted;
          priceWrap.append(original);
        }
        amount.className = 'gprow__price-amount';
        amount.setAttribute('data-gprow-price', '');
        amount.textContent = data.displayPriceFormatted || data.priceFormatted;
      }
      priceWrap.append(amount);
      if (data.displayPrice > 0 && data.salePercent > 0 && data.saleNote) {
        priceWrap.insertAdjacentHTML('beforeend', data.saleNote);
      }
    }

    // Part number
    const partEl = row.querySelector('[data-part-value]');
    if (partEl) {
      partEl.textContent = data.partNumber || '';
      const badge = partEl.closest('.gprow__part-badge');
      if (badge) badge.style.display = data.partNumber ? '' : 'none';
    }

    // Spec sheet
    const specBtn = row.querySelector('[data-gprow-spec-btn]');
    if (specBtn) {
      if (data.specSheetUrl) {
        specBtn.href = data.specSheetUrl;
        specBtn.style.display = '';
      } else {
        specBtn.style.display = 'none';
      }
    }

    // All product links
    row.querySelectorAll('a[href*="/products/"]').forEach(link => { link.href = data.url; });

    // Image link
    const imgLink = row.querySelector('.gprow__image-link');
    if (imgLink) imgLink.href = data.url;

    // Variant ID on add-to-cart button — swap to "Call Now" for $0
    const actionsWrap = row.querySelector('[data-gprow-actions-wrap]');
    if (actionsWrap) {
      const isZeroPrice = (data.displayPrice === 0 || data.price === 0);
      if (isZeroPrice) {
        actionsWrap.innerHTML = '<a href="tel:8583322358" class="gprow__call-btn button button--primary" data-gprow-call-btn><span>&#9743; Call Now</span></a>';
      } else {
        const existingCallBtn = actionsWrap.querySelector('[data-gprow-call-btn]');
        if (existingCallBtn) {
          // Restore qty + add-to-cart
          actionsWrap.innerHTML = '<div class="gprow__quantity" data-gprow-qty-wrap>' +
            '<button type="button" class="gprow__qty-btn gprow__qty-btn--minus" data-gprow-qty-change="-1" aria-label="Decrease quantity"><svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true"><path d="M0 1h10" stroke="currentColor" stroke-width="2"/></svg></button>' +
            '<input type="number" class="gprow__qty-input" value="1" min="1" max="999" data-gprow-qty-input aria-label="Quantity">' +
            '<button type="button" class="gprow__qty-btn gprow__qty-btn--plus" data-gprow-qty-change="1" aria-label="Increase quantity"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M5 0v10M0 5h10" stroke="currentColor" stroke-width="2"/></svg></button>' +
            '</div>' +
            '<button type="button" class="gprow__add-btn button button--primary" data-gprow-add-to-cart data-variant-id="' + data.variantId + '"><span class="gprow__add-btn-text" data-gprow-btn-text>Add to Cart</span><span class="gprow__add-btn-spinner" hidden aria-hidden="true"><svg class="spinner" viewBox="0 0 50 50"><circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle></svg></span></button>';
        } else {
          const addBtn = actionsWrap.querySelector('[data-gprow-add-to-cart]');
          if (addBtn) {
            addBtn.dataset.variantId = data.variantId;
            const available = data.available === true || data.available === 'true';
            addBtn.disabled = !available;
            const btnText = addBtn.querySelector('[data-gprow-btn-text]');
            if (btnText) btnText.textContent = available ? 'Add to Cart' : 'Sold Out';
          }
        }
      }
    }

    // Update row data attributes
    row.dataset.productId = data.productId;
    row.dataset.productHandle = data.handle;
  }

  /* ── Quantity Buttons ──────────────────────────────────── */

  bindQuantityButtons() {
    this.section.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gprow-qty-change]');
      if (!btn) return;
      e.preventDefault();
      const row = btn.closest('.gprow');
      if (!row) return;
      const input = row.querySelector('[data-gprow-qty-input]');
      if (!input) return;
      let val = parseInt(input.value, 10) || 1;
      val += parseInt(btn.dataset.gprowQtyChange, 10);
      if (val < 1) val = 1;
      if (val > 999) val = 999;
      input.value = val;
    });
  }

  /* ── Add to Cart ───────────────────────────────────────── */

  bindAddToCart() {
    this.section.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-gprow-add-to-cart]');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      const row = btn.closest('.gprow');
      if (!row) return;

      const variantId = btn.dataset.variantId;
      const qtyInput = row.querySelector('[data-gprow-qty-input]');
      const quantity = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;

      const textEl = btn.querySelector('[data-gprow-btn-text]');
      const spinnerEl = btn.querySelector('.gprow__add-btn-spinner');
      if (textEl) textEl.hidden = true;
      if (spinnerEl) spinnerEl.hidden = false;
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: quantity,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname
        })
      })
      .then(res => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then((response) => {
        if (textEl) textEl.textContent = 'Added!';
        if (textEl) textEl.hidden = false;
        if (spinnerEl) spinnerEl.hidden = true;
        btn.disabled = false;
        this.updateCartCount();
        this.updateCartDrawerSilent(response.sections);
        setTimeout(() => { if (textEl) textEl.textContent = 'Add to Cart'; }, 2000);
      })
      .catch(() => {
        if (textEl) textEl.textContent = 'Error';
        if (textEl) textEl.hidden = false;
        if (spinnerEl) spinnerEl.hidden = true;
        btn.disabled = false;
        setTimeout(() => { if (textEl) textEl.textContent = 'Add to Cart'; }, 2000);
      });
    });
  }

  updateCartCount() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      })
      .catch(() => {});
  }

  updateCartDrawerSilent(sections) {
    if (!sections || !sections['cart-drawer']) return;
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');

    const existingDrawer = document.querySelector('#CartDrawer');
    const newDrawer = newDoc.querySelector('#CartDrawer');

    // If transitioning from empty cart, replace the entire drawer content
    // (empty state has completely different HTML structure)
    const isCurrentlyEmpty = document.querySelector('cart-drawer.is-empty');
    if (isCurrentlyEmpty && newDrawer && existingDrawer) {
      existingDrawer.innerHTML = newDrawer.innerHTML;
      document.querySelector('cart-drawer').classList.remove('is-empty');
      const drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.remove('is-empty');
      return;
    }

    // Otherwise, surgically update form and footer
    const newForm = newDoc.querySelector('#CartDrawer-Form');
    const existingForm = document.querySelector('#CartDrawer-Form');
    if (newForm && existingForm) {
      existingForm.innerHTML = newForm.innerHTML;
    }

    const newFooter = newDoc.querySelector('.cart-drawer__footer');
    const existingFooter = document.querySelector('.cart-drawer__footer');
    if (newFooter && existingFooter) {
      existingFooter.innerHTML = newFooter.innerHTML;
    }
  }

  /* ── AJAX Group Deduplication Correction ─────────────── */

  async fetchAndCorrectGroups() {
    var handle = this.section.closest('[data-collection-handle]');
    if (!handle) return;
    handle = handle.dataset.collectionHandle;
    if (!handle) return;

    /* Build fetch URL preserving current filter/sort params */
    var currentParams = new URLSearchParams(window.location.search);
    currentParams.delete('view');
    currentParams.delete('page');
    var baseParams = currentParams.toString();

    try {
      var allProducts = [];
      var page = 1;
      var totalPages = 1;

      do {
        var url = '/collections/' + handle + '?view=grouped-products-data&page=' + page;
        if (baseParams) url += '&' + baseParams;
        var res = await fetch(url);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var data = await res.json();
        allProducts = allProducts.concat(data.products);
        totalPages = data.pagination.pages;
        page++;
      } while (page <= totalPages);

      /* Build productId → groupId lookup from AJAX data */
      var productGroupLookup = {};
      allProducts.forEach(function(p) {
        var gid = p.group_id || '';
        if (!gid && p.matched_products_id) {
          gid = String(p.matched_products_id).trim().toLowerCase();
        }
        if (gid) productGroupLookup[p.id] = gid;
      });

      /* Scan DOM in order: first occurrence of each group stays, duplicates hide */
      var seenGroups = {};
      var items = this.section.querySelectorAll('.product-grid__item[data-product-id]');

      items.forEach(function(li) {
        var pid = parseInt(li.dataset.productId, 10);
        var groupId = productGroupLookup[pid];

        if (!groupId) {
          /* Ungrouped — make sure it's visible */
          li.classList.remove('gprow--js-hidden');
          return;
        }

        if (seenGroups[groupId]) {
          /* Duplicate — hide it */
          li.classList.add('gprow--js-hidden');
        } else {
          /* Primary — show it */
          seenGroups[groupId] = pid;
          li.classList.remove('gprow--js-hidden');
        }
      });

    } catch (err) {
      console.warn('ProductGrouperV2: AJAX group correction failed, using Liquid fallback', err);
      /* On failure, just remove any JS-hidden classes so Liquid's work shows */
      this.section.querySelectorAll('.gprow--js-hidden').forEach(function(el) {
        el.classList.remove('gprow--js-hidden');
      });
    }
  }

  /* ── Filter/Sort Re-init ───────────────────────────────── */

  setupFilterListener() {
    const gridContainer = this.section.querySelector('#ProductGridContainer');
    if (gridContainer) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            setTimeout(() => this.reinit(), 100);
            break;
          }
        }
      });
      observer.observe(gridContainer, { childList: true, subtree: false });
    }

    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.contains(this.section) || e.target === this.section) {
        this.reinit();
      }
    });
  }

  reinit() {
    this.forceListView();
    this.buildAllOptionUIs();
    clearTimeout(this._correctTimeout);
    this._correctTimeout = setTimeout(() => this.fetchAndCorrectGroups(), 200);
  }
}

window.ProductGrouperV2 = ProductGrouperV2;
window.ProductGrouper = ProductGrouperV2;

/* End ProductGrouperV2 */

/* ============================================================
   SNIPPET: product-group-nav.liquid
   ProductGroupNav - Builds option matrix UI on product pages
   from variant titles. Clicking an option navigates to that
   product's page (not in-place update). Reuses same data
   schema and swatch/pill UI as ProductGrouperV2.
   ============================================================ */

class ProductGroupNav {
  constructor(container) {
    this.container = container;
    this.currentProductId = container.dataset.currentProductId;
    this.variants = [];
    this.options = [];
    this.prefixLen = 0;
    this.init();
  }

  init() {
    const variantsScript = this.container.querySelector('[data-group-variants]');
    if (!variantsScript) return;

    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.warn('ProductGroupNav: Failed to parse group data', e);
      return;
    }

    if (this.variants.length < 2) return;

    const colorMap = this._buildColorMap(this.variants);
    this.options = deriveOptionsFromTitles(this.variants, colorMap);
    if (this.options.length === 0) return;

    this.prefixLen = this._getCommonPrefixLength(this.variants);
    const uiContainer = this.container.querySelector('[data-pgn-options-ui]');
    if (uiContainer) {
      this.buildOptionUI(uiContainer);
      // Hide option groups irrelevant to the initially selected variant
      const currentVariant = this.variants.find(v => String(v.productId) === String(this.currentProductId)) || this.variants[0];
      if (currentVariant) {
        this.updateGroupVisibility(uiContainer, currentVariant);
      }
      this.bindClicks(uiContainer);
    }
  }

  /* ── Helpers ─────────────────────────────────────────── */

  _getCommonPrefixLength(variants) {
    if (variants.length < 2) return 0;
    const allParts = variants.map(v => v.title.split(' - '));
    const minLen = Math.min(...allParts.map(p => p.length));
    let common = 0;
    for (let i = 0; i < minLen; i++) {
      const vals = new Set(allParts.map(p => p[i]));
      if (vals.size === 1) common = i + 1;
      else break;
    }
    return common;
  }

  _buildColorMap(variants) {
    const colorMap = {};
    variants.forEach(v => {
      if (v.colors && typeof v.colors === 'object') {
        Object.entries(v.colors).forEach(([name, hex]) => {
          colorMap[name] = hex;
        });
      }
    });
    return colorMap;
  }

  _escAttr(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  _escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* ── Option Matrix Builder ──────────────────────────── */

  buildOptionUI(container) {
    const colorMap = this._buildColorMap(this.variants);
    const currentVariant = this.variants.find(v => String(v.productId) === String(this.currentProductId)) || this.variants[0];
    const currentParts = currentVariant.title.split(' - ');
    const currentOptions = currentParts.slice(this.prefixLen);

    let html = '';

    this.options.forEach(optionGroup => {
      const isColor = optionGroup.name === 'Color';
      const selectedValue = currentOptions[optionGroup.position] || '';

      html += '<div class="gprow__option-group" data-option-name="' + this._escAttr(optionGroup.name) + '" data-option-position="' + optionGroup.position + '">';
      html += '<span class="gprow__option-label">' + this._escHtml(optionGroup.name) + ':</span>';
      html += '<div class="gprow__option-values">';

      optionGroup.values.forEach(val => {
        const isSelected = val === selectedValue;
        const classes = ['gprow__swatch'];

        const hex = colorMap[val] || '';
        if (isColor && hex) {
          classes.push('gprow__swatch--color');
        } else {
          classes.push('gprow__swatch--pill');
        }
        if (isSelected) classes.push('gprow__swatch--selected');

        html += '<button type="button" class="' + classes.join(' ') + '"';
        if (hex) html += ' style="background-color: ' + hex + ';"';
        html += ' data-option-value="' + this._escAttr(val) + '"';
        html += ' data-option-position="' + optionGroup.position + '"';
        html += ' aria-label="' + this._escAttr(val) + '"';
        if (isColor && hex) html += ' data-color-name="' + this._escAttr(val) + '"';
        html += '>';
        if (!(isColor && hex)) html += this._escHtml(val);
        html += '</button>';
      });

      html += '</div></div>';
    });

    container.innerHTML = html;
  }

  /* ── Click Handling — Navigate to Product ───────────── */

  bindClicks(uiContainer) {
    uiContainer.addEventListener('click', (e) => {
      const swatch = e.target.closest('.gprow__swatch');
      if (!swatch) return;
      e.preventDefault();

      // Update visual selection in this option group
      const group = swatch.closest('.gprow__option-group');
      if (group) {
        group.querySelectorAll('.gprow__swatch').forEach(s => s.classList.remove('gprow__swatch--selected'));
        swatch.classList.add('gprow__swatch--selected');
      }

      // Pass clicked position so findBestVariant prioritizes user's choice
      const clickedPos = group ? group.dataset.optionPosition : null;

      // Find best matching variant (weighted toward the clicked group)
      const match = this.findBestVariant(uiContainer, clickedPos);
      if (match) {
        // Sync all other option groups to reflect the matched variant
        this.syncSelections(uiContainer, match);
        this.updateGroupVisibility(uiContainer, match);

        if (String(match.productId) !== String(this.currentProductId)) {
          window.location.href = match.url;
        } else {
          // Same product — update part number and spec sheet without navigating
          this.updateMeta(match);
        }
      }
    });
  }

  findBestVariant(uiContainer, clickedPos) {
    const selections = {};
    uiContainer.querySelectorAll('.gprow__option-group').forEach(group => {
      const pos = group.dataset.optionPosition;
      const selected = group.querySelector('.gprow__swatch--selected');
      if (selected) {
        selections[pos] = selected.dataset.optionValue;
      }
    });

    let best = null;
    let bestScore = -1;
    this.variants.forEach(v => {
      const parts = v.title.split(' - ');
      let score = 0;
      this.options.forEach(opt => {
        const variantVal = parts[this.prefixLen + opt.position];
        const selectedVal = selections[opt.position];
        if (selectedVal && variantVal === selectedVal) {
          score += (clickedPos != null && String(opt.position) === String(clickedPos)) ? 10 : 1;
        }
      });
      if (v.available) score += 100;
      if (score > bestScore) {
        bestScore = score;
        best = v;
      }
    });

    return best;
  }

  syncSelections(uiContainer, variant) {
    if (!variant.title) return;
    const parts = variant.title.split(' - ');
    uiContainer.querySelectorAll('.gprow__option-group').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition, 10);
      const expectedVal = parts[this.prefixLen + pos];
      if (!expectedVal) return;

      const currentSelected = group.querySelector('.gprow__swatch--selected');
      if (currentSelected && currentSelected.dataset.optionValue === expectedVal) return;

      const correctSwatch = group.querySelector('[data-option-value="' + CSS.escape(expectedVal) + '"]');
      if (correctSwatch) {
        group.querySelectorAll('.gprow__swatch').forEach(s => s.classList.remove('gprow__swatch--selected'));
        correctSwatch.classList.add('gprow__swatch--selected');
      }
    });
  }

  /* ── Show/Hide option groups based on selected variant branch ── */

  updateGroupVisibility(uiContainer, variant) {
    if (!this.variants || !this.options || this.options.length <= 1) return;

    const variantParts = variant.title.split(' - ');
    const variantSegCount = variantParts.length;

    // Find all variants with the same segment count (same structural "branch")
    const branchVariants = this.variants.filter(v => {
      return v.title.split(' - ').length === variantSegCount;
    });

    uiContainer.querySelectorAll('.gprow__option-group').forEach(group => {
      const pos = parseInt(group.dataset.optionPosition, 10);

      // Always show the first option group (branch selector)
      if (pos === 0) {
        group.style.display = '';
        return;
      }

      // Collect values that exist at this position in branch variants
      const validValues = new Set();
      branchVariants.forEach(v => {
        const p = v.title.split(' - ');
        const val = p[this.prefixLen + pos];
        if (val) validValues.add(val);
      });

      // Show/hide individual swatches
      let visibleCount = 0;
      group.querySelectorAll('.gprow__swatch').forEach(swatch => {
        const val = swatch.dataset.optionValue;
        if (validValues.has(val)) {
          swatch.style.display = '';
          visibleCount++;
        } else {
          swatch.style.display = 'none';
          swatch.classList.remove('gprow__swatch--selected');
        }
      });

      // Hide entire group if 0 or 1 visible swatches
      if (visibleCount <= 1) {
        group.style.display = 'none';
      } else {
        group.style.display = '';
      }
    });
  }

  updateMeta(data) {
    const partEl = this.container.querySelector('[data-pgn-part-number]');
    if (partEl) {
      if (data.partNumber) {
        partEl.innerHTML = 'Part #: <strong>' + this._escHtml(data.partNumber) + '</strong>';
        partEl.style.display = '';
      } else {
        partEl.style.display = 'none';
      }
    }
    const specEl = this.container.querySelector('[data-pgn-spec-sheet]');
    if (specEl) {
      if (data.specSheetUrl) {
        specEl.href = data.specSheetUrl;
        specEl.style.display = '';
      } else {
        specEl.style.display = 'none';
      }
    }
  }
}

window.ProductGroupNav = ProductGroupNav;

/* End ProductGroupNav */

/* ============================================================
   FIX: Preload screen white flash on navigation
   (layout/theme.liquid, assets/global.js)
   The theme's beforeunload handler removes 'off' and 'loaded'
   classes from the preload screen, causing it to flash white
   on every page navigation. This neutralizes that behavior
   when the preloading logo screen is disabled.
   ============================================================ */
(function() {
  var preload = document.querySelector('.preload-screen[data-preload-screen-page]');
  if (preload) {
    /* Immediately mark as loaded so content is visible */
    preload.classList.add('off', 'loaded');
    preload.setAttribute('loaded', 'true');
    document.body.classList.add('off', 'loaded');

    /* Override beforeunload to prevent white flash on navigation */
    window.addEventListener('beforeunload', function(e) {
      preload.style.display = 'none';
    }, true);
  }
})();
/* ============================================================
   END FIX: Preload screen white flash on navigation
   ============================================================ */

/* ============================================================
   FIX: Vertical menu mega menu transition override
   File: snippets/header-menu-vertical.liquid

   Problem: The default DropdownDetails.transitionIn() slides
   the mega menu content from translateY(-20%) to translateY(0),
   which causes the panel to briefly overlap other vertical menu
   items during animation, triggering their hover mega menus.

   Fix: Override transitionIn/Out on vertical menu mega-menu
   details elements to use a horizontal slide (from left) instead
   of the vertical slide, so the panel never crosses other items.
   ============================================================ */
(function() {
  function patchVerticalMenuTransitions() {
    const verticalMenu = document.querySelector('.header__vertical-menu');
    if (!verticalMenu) return;

    // Patch the OUTER vertical menu dropdown (the category list itself)
    // so it doesn't slide down from above and trigger top item hovers
    if (verticalMenu.transitionIn) {
      verticalMenu.transitionIn = function() {
        if (!this.elements.dropdown || !this.elements.dropdown.firstElementChild) return;
        Motion.animate(this.elements.dropdown, { opacity: [0, 1], visibility: 'visible', overflow: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.15, easing: [.7, 0, .2, 1] });
        return Motion.animate(this.elements.dropdown.firstElementChild, { transform: ['translateY(8px)', 'translateY(0)'], opacity: [0, 1] }, { duration: theme.config.motionReduced ? 0 : 0.2, easing: [.7, 0, .2, 1] }).finished;
      };
      verticalMenu.transitionOut = function() {
        if (!this.elements.dropdown || !this.elements.dropdown.firstElementChild) return;
        Motion.animate(this.elements.dropdown, { opacity: 0, visibility: 'hidden', overflow: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.1, easing: [.7, 0, .2, 1] });
        return Motion.animate(this.elements.dropdown.firstElementChild, { transform: 'translateY(8px)', opacity: 0 }, { duration: theme.config.motionReduced ? 0 : 0.15, easing: [.7, 0, .2, 1] }).finished;
      };
    }

    // Patch the INNER mega menu panels (hover submenus)
    // Use horizontal slide so panels never cross other menu items
    const megaMenuDetails = verticalMenu.querySelectorAll('details.mega-menu[is="dropdown-details"]');
    megaMenuDetails.forEach(function(details) {
      details.transitionIn = function() {
        if (!this.elements.dropdown || !this.elements.dropdown.firstElementChild) return;
        Motion.animate(this.elements.dropdown, { opacity: [0, 1], visibility: 'visible', overflow: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.2, easing: [.7, 0, .2, 1] });
        return Motion.animate(this.elements.dropdown.firstElementChild, { transform: ['translateX(-8px)', 'translateX(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] }).finished;
      };
      details.transitionOut = function() {
        if (!this.elements.dropdown || !this.elements.dropdown.firstElementChild) return;
        Motion.animate(this.elements.dropdown, { opacity: 0, visibility: 'hidden', overflow: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.15, easing: [.7, 0, .2, 1] });
        return Motion.animate(this.elements.dropdown.firstElementChild, { transform: 'translateX(-8px)' }, { duration: theme.config.motionReduced ? 0 : 0.2, easing: [.7, 0, .2, 1] }).finished;
      };
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(patchVerticalMenuTransitions, 100);
    });
  } else {
    setTimeout(patchVerticalMenuTransitions, 100);
  }
})();
/* ============================================================
   END FIX: Vertical menu mega menu transition override
   ============================================================ */

/* ============================================================
   BLOCK: Call for Pricing — $0.00 variant handling
   Files: blocks/buy-buttons.liquid, snippets/price.liquid
   ============================================================ */
(function() {
  function handleCallForPricing() {
    document.addEventListener('variant:changed', function(e) {
      var variant = e.detail || {};
      var section = e.target.closest('[data-section-id]') || document;
      var priceContainer = section.querySelector('.price__container');
      var buyButtonsBlock = section.querySelector('.buy-buttons-block');

      if (!variant.price && variant.price !== 0) return;

      if (variant.price === 0) {
        /* Update price display */
        if (priceContainer) {
          var existingCallText = priceContainer.querySelector('.call-for-pricing-text');
          if (!existingCallText) {
            priceContainer.setAttribute('data-original-html', priceContainer.innerHTML);
            priceContainer.innerHTML = '<span class="call-for-pricing-text">Please call us for pricing</span>';
          }
        }
        /* Update add-to-cart button */
        if (buyButtonsBlock) {
          var form = buyButtonsBlock.querySelector('product-form-component');
          var existingCallBtn = buyButtonsBlock.querySelector('.call-for-pricing-button');
          if (form && !existingCallBtn) {
            form.setAttribute('data-hidden-by-cfp', 'true');
            form.style.display = 'none';
            var callBtnWrap = document.createElement('div');
            callBtnWrap.className = 'product-form__buttons spacing-style call-for-pricing-wrapper';
            callBtnWrap.innerHTML = '<a href="tel:8583322358" class="button product-form__submit button--full-width call-for-pricing-button"><span class="call-for-pricing-button__icon">&#9743;</span> Call for Pricing</a>';
            form.parentNode.insertBefore(callBtnWrap, form.nextSibling);
          }
        }
      } else {
        /* Restore price display */
        if (priceContainer) {
          var origHtml = priceContainer.getAttribute('data-original-html');
          if (origHtml) {
            priceContainer.innerHTML = origHtml;
            priceContainer.removeAttribute('data-original-html');
          }
        }
        /* Restore add-to-cart button */
        if (buyButtonsBlock) {
          var form = buyButtonsBlock.querySelector('product-form-component[data-hidden-by-cfp]');
          var callWrap = buyButtonsBlock.querySelector('.call-for-pricing-wrapper');
          if (form) {
            form.style.display = '';
            form.removeAttribute('data-hidden-by-cfp');
          }
          if (callWrap) {
            callWrap.remove();
          }
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleCallForPricing);
  } else {
    handleCallForPricing();
  }
})();
/* ============================================================
   END BLOCK: Call for Pricing — $0.00 variant handling
   ============================================================ */

/* ============================================================
   BLOCK: Grid View Switcher — grouped collection pages
   File: sections/main-collection-grouped.liquid
   ============================================================ */
(function() {
  function initGpviewSwitcher() {
    var switcher = document.querySelector('[data-gpview-switcher]');
    if (!switcher) return;

    var collection = document.querySelector('.main-collection-grouped .collection[data-gpview], .main-search-grouped .collection[data-gpview]');
    if (!collection) return;

    /* Relocate switcher into the sorting-wrapper bar (left of Items per page / Sort by) */
    var sortingWrapper = document.querySelector('.main-collection-grouped .sorting-wrapper, .main-search-grouped .sorting-wrapper');
    if (sortingWrapper) {
      sortingWrapper.insertBefore(switcher, sortingWrapper.firstChild);
    }
    switcher.style.display = '';

    var buttons = switcher.querySelectorAll('[data-gpview]');
    var storageKey = 'gpview-mode';

    /* Restore saved view */
    var saved = sessionStorage.getItem(storageKey);
    if (saved) {
      collection.setAttribute('data-gpview', saved);
      buttons.forEach(function(btn) {
        btn.classList.toggle('gpview-switcher__btn--active', btn.getAttribute('data-gpview') === saved);
      });
    }

    /* Click handler */
    buttons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var view = btn.getAttribute('data-gpview');
        collection.setAttribute('data-gpview', view);
        sessionStorage.setItem(storageKey, view);
        buttons.forEach(function(b) {
          b.classList.toggle('gpview-switcher__btn--active', b.getAttribute('data-gpview') === view);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGpviewSwitcher);
  } else {
    initGpviewSwitcher();
  }
})();
/* ============================================================
   END BLOCK: Grid View Switcher
   ============================================================ */

/* ============================================================
   START BLOCK: Footer Menu Accordions — Open by Default on Mobile
   Related file: blocks/menu.liquid (accordion-custom in footer)
   ============================================================ */
(function() {
  function openFooterAccordionsOnMobile() {
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (!isMobile) return;

    var footerAccordions = document.querySelectorAll('.section-footer accordion-custom.menu--accordion');
    footerAccordions.forEach(function(accordion) {
      var details = accordion.querySelector('details');
      if (details) {
        details.open = true;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFooterAccordionsOnMobile);
  } else {
    openFooterAccordionsOnMobile();
  }
})();
/* ============================================================
   END BLOCK: Footer Menu Accordions — Open by Default on Mobile
   ============================================================ */

/* ============================================================
   START BLOCK: Dealer Checkout Interception — Draft Order Flow
   Related file: layout/theme.liquid (window.__dealer injection)
   Related endpoint: /apps/dealer-pricing/create-draft (App Proxy)
   ============================================================ */
(function () {
  var dealer = window.__dealer;
  if (!dealer || !dealer.tags) return;

  var DEALER_TAGS = ['dealer', 'dealer2', 'dealer3'];
  var lowerTags = dealer.tags.map(function (t) { return t.toLowerCase(); });
  var isDealer = DEALER_TAGS.some(function (dt) { return lowerTags.indexOf(dt) !== -1; });
  if (!isDealer) return;

  document.documentElement.classList.add('is-dealer-customer');

  var PROXY_URL = '/apps/dealer-pricing/create-draft';
  var overlay = null;

  function createOverlay() {
    var el = document.createElement('div');
    el.id = 'dealer-checkout-overlay';
    el.innerHTML =
      '<div class="dealer-checkout-overlay__inner">' +
        '<div class="dealer-checkout-overlay__spinner"></div>' +
        '<p class="dealer-checkout-overlay__text">Preparing your dealer order&hellip;</p>' +
      '</div>';
    document.body.appendChild(el);
    return el;
  }

  function showOverlay() {
    if (!overlay) overlay = createOverlay();
    overlay.classList.add('is-active');
  }

  function hideOverlay() {
    if (overlay) overlay.classList.remove('is-active');
  }

  function showError(msg) {
    hideOverlay();
    var existing = document.getElementById('dealer-checkout-error');
    if (existing) existing.remove();

    var el = document.createElement('div');
    el.id = 'dealer-checkout-error';
    el.setAttribute('role', 'alert');
    el.textContent = msg || 'Could not prepare your dealer checkout. Please try again or call (858) 332-2358 for assistance.';
    document.body.appendChild(el);
    setTimeout(function () { el.remove(); }, 6000);
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    }).then(function (r) { return r.json(); });
  }

  function createDraftOrder(cartItems) {
    var items = cartItems.map(function (item) {
      return { variant_id: item.id, quantity: item.quantity };
    });

    return fetch(PROXY_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) throw new Error(body.error || 'Server error');
        return body;
      });
    });
  }

  function handleDealerCheckout(e) {
    e.preventDefault();
    e.stopPropagation();
    showOverlay();

    fetchCart()
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          hideOverlay();
          return;
        }
        return createDraftOrder(cart.items);
      })
      .then(function (result) {
        if (!result) return;
        if (result.invoiceUrl) {
          window.location.href = result.invoiceUrl;
        } else {
          throw new Error('No invoice URL returned');
        }
      })
      .catch(function (err) {
        console.error('[Dealer Checkout]', err);
        showError();
      });
  }

  function hookCheckoutButtons() {
    var selectors = [
      '#checkout',
      '.cart__checkout-button',
      'button[name="checkout"]',
      'input[name="checkout"]',
    ];

    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (btn) {
        if (btn.dataset.dealerHooked) return;
        btn.dataset.dealerHooked = 'true';
        btn.addEventListener('click', handleDealerCheckout, true);
      });
    });

    var dynamicBtns = document.querySelectorAll(
      '.additional-checkout-buttons button, ' +
      '.additional-checkout-buttons .shopify-payment-button__button, ' +
      '.cart__dynamic-checkout-buttons button'
    );
    dynamicBtns.forEach(function (btn) {
      if (btn.dataset.dealerHooked) return;
      btn.dataset.dealerHooked = 'true';
      btn.addEventListener('click', handleDealerCheckout, true);
    });
  }

  function interceptFormSubmit() {
    document.querySelectorAll('form[action="/cart"], form[action="/checkout"]').forEach(function (form) {
      if (form.dataset.dealerHooked) return;
      form.dataset.dealerHooked = 'true';
      form.addEventListener('submit', handleDealerCheckout, true);
    });
  }

  function init() {
    hookCheckoutButtons();
    interceptFormSubmit();

    var observer = new MutationObserver(function () {
      hookCheckoutButtons();
      interceptFormSubmit();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
/* ============================================================
   END BLOCK: Dealer Checkout Interception — Draft Order Flow
   ============================================================ */
